import { FC, useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom"; // 添加 useSearchParams
import { Footer } from "../../components/footer/Footer";
import { Button, Upload, Input, message, Card, Space, Divider, Modal, Switch, Select } from "antd";
import { ArrowLeftOutlined, UploadOutlined, CloudUploadOutlined, VideoCameraOutlined, FileTextOutlined, FileTextOutlined as SubtitleOutlined, QuestionCircleOutlined, DeleteOutlined, PlayCircleOutlined } from "@ant-design/icons";
import { getSeriesListApi } from "../../service/series";
import { uploadSingleFileToTos, uploadVideoAndScriptToTos, submitResourceApi, delTaskApi } from "../../service/myWork";
import { getTaskListApi } from "../../service/folders";
import { getPureVideoListApi } from "../../service/task";
import { useAlert } from "../../hooks/useAlert";
import { AlertMessage, BackButton } from "../../components/common"; // 添加 BackButton 导入
import { ALERT_Z_INDEX } from "../../utils/alertManager";
import { processVideoUrl } from "../../utils/videoUrl";
import { VIDEO_CDN_CONFIG, getEnv, getVideoStoragePrefix } from "../../config/video";
import "../../styles/reset.scss";
import "./UploadVideo.scss";

interface Props { }

interface VideoFile {
    uid: string;
    name: string;
    size: number;
    type: string;
    status: 'done' | 'uploading' | 'error';
    url?: string;
    duration?: number;
    thumbnail?: string;
    file?: File;
    previewUrl?: string;
    resourceId?: number; // 新增：存储上传后的资源ID
    decodeFailed?: boolean; // 浏览器无法解码（如 H.265）时为 true
}

interface ScriptFile {
    uid: string;
    name: string;
    size: number;
    type: string;
    status: 'done' | 'uploading' | 'error';
    file?: File;
    resourceId?: number; // 新增：存储上传后的资源ID
}

interface SubtitleFile {
    uid: string;
    name: string;
    size: number;
    type: string;
    status: 'done' | 'uploading' | 'error';
    file?: File;
    resourceId?: number; // 新增：存储上传后的资源ID
}

export const UploadVideo: FC<Props> = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams(); // 添加这行
    // 新增：使用封装的Alert组件
    const { alertConfig, showAlert, hideAlert, showWarning, showError } = useAlert();
    const [videoName, setVideoName] = useState('');
    const [modelConfig, setModelConfig] = useState('');
    const [videoFiles, setVideoFiles] = useState<VideoFile[]>([]);
    const [scriptFiles, setScriptFiles] = useState<ScriptFile[]>([]);
    const [subtitleFiles, setSubtitleFiles] = useState<SubtitleFile[]>([]);
    const [uploadScript, setUploadScript] = useState(false);
    const [uploadSubtitle, setUploadSubtitle] = useState(false);
    const [showSubtitleTooltip, setShowSubtitleTooltip] = useState(false);
    const [selectedVideoIndex, setSelectedVideoIndex] = useState<number>(-1);

    // 新增：生成弹窗相关状态
    const [generateModalVisible, setGenerateModalVisible] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);

    // 新增：目标语言和字幕擦除设置
    const [targetLanguage, setTargetLanguage] = useState(''); // 移除硬编码的'日语'
    const [subtitleErase, setSubtitleErase] = useState(false);

    // 新增：剧集信息状态
    const [seriesName, setSeriesName] = useState('');
    const [isLoadingSeries, setIsLoadingSeries] = useState(true);
    
    // 新增：视频数据加载状态
    const [isLoadingVideos, setIsLoadingVideos] = useState(true);

    // 新增：提醒弹窗相关状态
    const [alertModalVisibleK, setAlertModalVisibleK] = useState(false);
    const [alertModalVisible, setAlertModalVisible] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertTitle, setAlertTitle] = useState('');

    // 新增：同名文件检测相关状态
    const [duplicateFileModalVisible, setDuplicateFileModalVisible] = useState(false);
    const [duplicateFileInfo, setDuplicateFileInfo] = useState<{
        fileName: string;
        fileType: 'video' | 'script' | 'subtitle';
        existingFile: any;
        newFile: File;
        allFiles?: File[]; // 新增：存储当前批次的所有文件
    } | null>(null);

    // 新增：字幕匹配不成功弹窗相关状态
    const [subtitleMatchModalVisible, setSubtitleMatchModalVisible] = useState(false);
    const [subtitleMatchInfo, setSubtitleMatchInfo] = useState<{
        invalidFiles: { file: File; name: string }[];
        allFiles: File[];
        subtitleFiles: SubtitleFile[];
    } | null>(null);

    // 新增：控制视频名称显示状态
    const [showVideoName, setShowVideoName] = useState(false);

    // 视频元素引用
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
    
    // 优化：添加视频缩略图缓存
    const thumbnailCache = useRef<Map<string, string>>(new Map());
    
    // 优化：添加虚拟化状态
    const [visibleRange, setVisibleRange] = useState({ start: 0, end: 11 }); // 初始显示11个视频
    
    // 优化：添加预加载状态
    const [isPreloading, setIsPreloading] = useState(false);
    
    // 优化：预加载函数，在页面进入前就开始准备数据
    const preloadVideoData = useCallback(async () => {
        const currentFolderId = searchParams.get('seriesId');
        const isPure = searchParams.get('isPure') === 'true';
        if (!currentFolderId || currentFolderId === '0') {
            return;
        }
        
        setIsPreloading(true);
        try {
            // 预加载视频数据，但不更新UI
            const seriesId = parseInt(currentFolderId);
            let waitingVideosList: Task.TaskItem[] = [];
            
            if (isPure) {
                // 纯净版：调用纯净版视频列表接口
                const response = await getPureVideoListApi(seriesId);
                if (response.code === 0 && response.data) {
                    waitingVideosList = response.data
                        .filter((item: Task.PureVideoListItem) => item.status === 0)
                        .map((item: Task.PureVideoListItem) => ({
                            name: item.name.replace(/\.[^/.]+$/, ''),
                            disable_bgm: item.disableBgm ?? false,
                            has_user_srt: item.hasUserSrt,
                            has_user_script: item.hasUserScript,
                            tag: item.tag,
                            descript: item.description,
                            id: item.id,
                            series_id: seriesId,
                            user_id: 0,
                            status: item.status,
                            create_time: item.createTime,
                            update_time: item.updateTime,
                            file_url: item.fileUrl,
                            source_hls_url: null,
                            result_url: item.resultUrl,
                            translated_subtitle_url: null,
                            subtitle_url: item.subtitleUrl,
                            pure_video_url: item.pureVideoUrl,
                            pure_video_hls_url: null,
                            cover_url: item.coverUrl,
                            backgroud_audio_url: null,
                            script_url: item.scriptUrl,
                            duration: item.duration ?? undefined,
                            sourceLanguage: item.langCode,
                            targetLanguage: "",
                            needRemovalSubtitle: false,
                            episodeTasks: [],
                        }));
                }
            } else {
                // 多语种版本：调用多语种视频列表接口
                const response = await getTaskListApi({ seriesId, langCode: 'en' });
                waitingVideosList = response.data.filter((video: Task.TaskItem) => video.status === 0);
            }
            
            // 将数据存储在缓存中，供后续使用
            if (waitingVideosList.length > 0) {
                console.log('预加载完成，准备显示', waitingVideosList.length, '个视频');
            }
        } catch (error) {
            console.warn('预加载失败:', error);
        } finally {
            setIsPreloading(false);
        }
    }, [searchParams]);

    // 新增：临时存储待处理的文件
    const [pendingFiles, setPendingFiles] = useState<{
        video: File[];
        script: File[];
        subtitle: File[];
    }>({
        video: [],
        script: [],
        subtitle: []
    });

    // 新增：存储当前批次待处理的文件
    const [currentBatchFiles, setCurrentBatchFiles] = useState<{
        video: File[];
        script: File[];
        subtitle: File[];
    }>({
        video: [],
        script: [],
        subtitle: []
    });

    // 新增：存储每个文件的上传进度 (fileKey -> progress)
    const [uploadProgress, setUploadProgress] = useState<Map<string, number>>(new Map());

    // 新增：存储待确认的文件批次
    const [pendingFileBatch, setPendingFileBatch] = useState<{
        files: File[];
        fileType: 'video' | 'script' | 'subtitle';
        hasDuplicate: boolean;
        duplicateFileNames: string[]; // 新增：存储重复文件名列表
    } | null>(null);

    // 新增：存储最近添加的文件，用于取消时删除
    const [recentlyAddedFiles, setRecentlyAddedFiles] = useState<{
        video: VideoFile[];
        script: any[];
        subtitle: any[];
    }>({
        video: [],
        script: [],
        subtitle: []
    });

    // 新增：存储已上传的资源ID
    const [uploadedResourceIds, setUploadedResourceIds] = useState<number[]>([]);
    
    // 新增：存储所有的task ID
    const [taskIds, setTaskIds] = useState<number[]>([]);
    
    
    // 新增：上传状态管理
    const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set());
    
    // 新增：存储上传任务的AbortController，用于取消上传
    const [uploadControllers, setUploadControllers] = useState<Map<string, AbortController>>(new Map());

    // 优化：获取状态为0的等待视频列表，支持缓存和快速响应
    const fetchWaitingVideos = async () => {
        try {
            // 从URL参数获取剧集ID和是否纯净版
            let currentFolderId = searchParams.get('seriesId');
            const isPure = searchParams.get('isPure') === 'true';
            
            if (!currentFolderId || currentFolderId === '0') {
                console.log('未选择剧集，无法获取等待视频列表');
                return [];
            }

            const seriesId = parseInt(currentFolderId);
            console.log('获取等待视频列表，剧集ID：', seriesId, '是否纯净版：', isPure);
            
            // 优化：使用AbortController支持请求取消
            const abortController = new AbortController();
            const timeoutId = setTimeout(() => abortController.abort(), 10000); // 10秒超时
            
            try {
                let waitingVideosList: Task.TaskItem[] = [];
                
                if (isPure) {
                    // 纯净版：调用纯净版视频列表接口
                    const response = await getPureVideoListApi(seriesId);
                    clearTimeout(timeoutId);
                    console.log('纯净版视频列表响应：', response);
                    
                    if (response.code === 0 && response.data) {
                        // 将纯净版视频列表转换为 Task.TaskItem 格式
                        waitingVideosList = response.data
                            .filter((item: Task.PureVideoListItem) => item.status === 0)
                            .map((item: Task.PureVideoListItem) => {
                                // 处理封面URL：统一使用 processVideoUrl，避免重复拼接 /storage（例如 /storage/storage/...）
                                const coverUrl = item.coverUrl ? processVideoUrl(item.coverUrl, false) : null;
                                
                                return {
                                    name: item.name.replace(/\.[^/.]+$/, ''), // 移除文件后缀
                                    disable_bgm: item.disableBgm ?? false,
                                    has_user_srt: item.hasUserSrt,
                                    has_user_script: item.hasUserScript,
                                    tag: item.tag,
                                    descript: item.description,
                                    id: item.id,
                                    series_id: seriesId,
                                    user_id: 0,
                                    status: item.status,
                                    create_time: item.createTime,
                                    update_time: item.updateTime,
                                    file_url: item.fileUrl,
                                    source_hls_url: null,
                                    result_url: item.resultUrl,
                                    translated_subtitle_url: null,
                                    subtitle_url: item.subtitleUrl,
                                    pure_video_url: item.pureVideoUrl,
                                    pure_video_hls_url: null,
                                    cover_url: coverUrl, // 使用处理后的封面URL
                                    backgroud_audio_url: null,
                                    script_url: item.scriptUrl,
                                    duration: item.duration ?? undefined,
                                    sourceLanguage: item.langCode,
                                    targetLanguage: "",
                                    needRemovalSubtitle: false,
                                    episodeTasks: [],
                                };
                            });
                    }
                } else {
                    // 多语种版本：调用多语种视频列表接口
                    // 这里语言编码暂时使用 'en'，仅用于获取等待视频列表
                    const response = await getTaskListApi({ seriesId, langCode: 'en' });
                    clearTimeout(timeoutId);
                    console.log('多语种视频列表响应：', response);
                    
                    // 筛选状态为0的视频
                    waitingVideosList = response.data.filter((video: Task.TaskItem) => video.status === 0);
                }
                
                console.log('状态为0的等待视频：', waitingVideosList);
                return waitingVideosList;
            } catch (error: any) {
                clearTimeout(timeoutId);
                throw error;
            }
        } catch (error) {
            console.error('获取等待视频列表失败:', error);
            // 优化：不显示错误消息，避免干扰用户体验
            return [];
        }
    };

    // 优化：将等待状态的视频数据转换为上传页面所需的格式，提高转换效率
    const convertWaitingVideosToUploadFormat = (waitingVideosList: Task.TaskItem[]) => {
        // 优化：使用更高效的数据转换，减少不必要的计算
        const convertedVideos: VideoFile[] = waitingVideosList.map((video, index) => {
            // 预计算常用值，避免重复计算
            const videoId = video.id;
            const videoName = video.name || `视频_${videoId}`;
            const videoDuration = typeof video.duration === 'number' && video.duration > 0 ? video.duration : undefined;
            
            // 处理封面URL：统一使用 processVideoUrl，避免重复拼接 /storage（例如 /storage/storage/...）
            const thumbnail = video.cover_url ? processVideoUrl(video.cover_url, false) : undefined;
            
            return {
                uid: `waiting-${videoId}-${index}`,
                name: videoName,
                size: 0, // 等待状态的视频没有文件大小信息
                type: 'video/mp4', // 默认类型
                status: 'done' as const, // 标记为已完成状态，因为已经上传到后端
                url: video.file_url || undefined,
                duration: videoDuration,
                thumbnail, // 使用处理后的封面URL
                resourceId: videoId // 存储资源ID
            };
        });
        
        console.log(`转换完成，共 ${convertedVideos.length} 个等待视频`);
        return convertedVideos;
    };

    // 新增：上传视频文件到后端的函数
    const uploadVideoToBackend = async (files: File[]) => {
        const fileKeys = files.map(f => `video-${f.name}-${f.size}`);
        
        // 检查是否已经在上传中
        const alreadyUploading = fileKeys.some(key => uploadingFiles.has(key));
        if (alreadyUploading) {
            console.log('视频文件正在上传中，跳过重复上传');
            return Promise.resolve([]); // 返回空数组，避免undefined
        }

        // 添加上传状态
        setUploadingFiles(prev => {
            const newSet = new Set(prev);
            fileKeys.forEach(key => newSet.add(key));
            return newSet;
        });

        // 创建AbortController用于取消上传
        const abortController = new AbortController();
        fileKeys.forEach(key => {
            setUploadControllers(prev => new Map(prev).set(key, abortController));
        });

        try {
            // 从URL参数获取剧集ID
            let currentFolderId = searchParams.get('seriesId');
            
            if (!currentFolderId || currentFolderId === '0') {
                message.error('请先选择剧集文件夹');
                throw new Error('请先选择剧集文件夹');
            }

            const seriesId = parseInt(currentFolderId!);
            const projectName = videoName.trim() || (videoFiles.length > 1 ? '多视频项目' : videoFiles[0]?.name || '未命名项目');

            console.log('上传视频文件到后端，参数：', { files, seriesId, projectName });

            // 使用新的上传接口，逐个上传文件
            const newResourceIds: number[] = [];
            const newTaskIds: number[] = [];

            for (const file of files) {
                const fileKey = `video-${file.name}-${file.size}`;
                try {
                    // 初始化进度为0
                    setUploadProgress(prev => new Map(prev).set(fileKey, 0));
                    
                    // 上传文件，带进度回调
                    const taskId = await uploadSingleFileToTos(
                        seriesId, 
                        file, 
                        'video',
                        (progress) => {
                            setUploadProgress(prev => new Map(prev).set(fileKey, progress));
                        },
                        abortController.signal
                    );
                    
                    newResourceIds.push(taskId);
                    newTaskIds.push(taskId);
                    console.log(`视频文件 ${file.name} 上传成功，任务ID：`, taskId);
                    
                    // 上传完成，移除进度
                    setUploadProgress(prev => {
                        const newMap = new Map(prev);
                        newMap.delete(fileKey);
                        return newMap;
                    });
                } catch (error: any) {
                    console.error(`视频文件 ${file.name} 上传失败:`, error);
                    // 上传失败，移除进度
                    setUploadProgress(prev => {
                        const newMap = new Map(prev);
                        newMap.delete(fileKey);
                        return newMap;
                    });
                    throw new Error(`视频文件 ${file.name} 上传失败: ${error.message}`);
                }
            }

            if (newResourceIds.length > 0) {
                setUploadedResourceIds(prev => [...prev, ...newResourceIds]);
                console.log('视频文件上传到后端成功，资源ID：', newResourceIds);
                console.log('视频响应数据中data的id列表：', newTaskIds);
                
                // 将新的task ID添加到全局taskIds数组中
                setTaskIds(prev => [...prev, ...newTaskIds]);
                console.log('更新后的全局taskIds数组：', [...taskIds, ...newTaskIds]);
                
                return newResourceIds; // 返回资源ID数组
            } else {
                throw new Error('没有文件上传成功');
            }
        } catch (error: any) {
            console.error('视频文件上传到后端失败:', error);
            // 使用全局 AlertMessage 提示具体失败原因
            showError(error.message || '视频文件上传失败，请重试');
            throw error;
        } finally {
            // 移除上传状态
            setUploadingFiles(prev => {
                const newSet = new Set(prev);
                fileKeys.forEach(key => newSet.delete(key));
                return newSet;
            });
            
            // 清理进度
            setUploadProgress(prev => {
                const newMap = new Map(prev);
                fileKeys.forEach(key => newMap.delete(key));
                return newMap;
            });
            
            // 清理AbortController
            fileKeys.forEach(key => {
                setUploadControllers(prev => {
                    const newMap = new Map(prev);
                    newMap.delete(key);
                    return newMap;
                });
            });
        }
    };

    // 新增：上传剧本文件到后端的函数
    const uploadScriptToBackend = async (files: File[]) => {
        const fileKeys = files.map(f => `script-${f.name}-${f.size}`);
        
        // 检查是否已经在上传中
        const alreadyUploading = fileKeys.some(key => uploadingFiles.has(key));
        if (alreadyUploading) {
            console.log('剧本文件正在上传中，跳过重复上传');
            return Promise.resolve([]); // 返回空数组，避免undefined
        }

        // 添加上传状态
        setUploadingFiles(prev => {
            const newSet = new Set(prev);
            fileKeys.forEach(key => newSet.add(key));
            return newSet;
        });

        // 创建AbortController用于取消上传
        const abortController = new AbortController();
        fileKeys.forEach(key => {
            setUploadControllers(prev => new Map(prev).set(key, abortController));
        });

        try {
            // 从URL参数获取剧集ID
            let currentFolderId = searchParams.get('seriesId');
            
            if (!currentFolderId || currentFolderId === '0') {
                message.error('请先选择剧集文件夹');
                throw new Error('请先选择剧集文件夹');
            }

            const seriesId = parseInt(currentFolderId!);
            // 修复：剧本上传时使用剧本文件名作为项目名称
            const projectName = videoName.trim() || (files.length > 1 ? '多剧本项目' : files[0]?.name || '未命名项目');

            console.log('上传剧本文件到后端，参数：', { files, seriesId, projectName });

            // 使用新的上传接口，逐个上传文件
            const newResourceIds: number[] = [];
            const newTaskIds: number[] = [];

            for (const file of files) {
                try {
                    const taskId = await uploadSingleFileToTos(seriesId, file, 'script');
                    newResourceIds.push(taskId);
                    newTaskIds.push(taskId);
                    console.log(`剧本文件 ${file.name} 上传成功，任务ID：`, taskId);
                } catch (error: any) {
                    console.error(`剧本文件 ${file.name} 上传失败:`, error);
                    throw new Error(`剧本文件 ${file.name} 上传失败: ${error.message}`);
                }
            }

            if (newResourceIds.length > 0) {
                setUploadedResourceIds(prev => [...prev, ...newResourceIds]);
                console.log('剧本文件上传到后端成功，资源ID：', newResourceIds);
                console.log('剧本响应数据中data的id列表：', newTaskIds);
                
                // 将新的task ID添加到全局taskIds数组中
                setTaskIds(prev => [...prev, ...newTaskIds]);
                console.log('更新后的全局taskIds数组：', [...taskIds, ...newTaskIds]);
                
                return newResourceIds; // 返回资源ID数组
            } else {
                throw new Error('没有文件上传成功');
            }
        } catch (error: any) {
            console.error('剧本文件上传到后端失败:', error);
            message.error(error.message || '剧本文件上传失败，请重试');
            throw error;
        } finally {
            // 移除上传状态
            setUploadingFiles(prev => {
                const newSet = new Set(prev);
                fileKeys.forEach(key => newSet.delete(key));
                return newSet;
            });
            
            // 清理AbortController
            fileKeys.forEach(key => {
                setUploadControllers(prev => {
                    const newMap = new Map(prev);
                    newMap.delete(key);
                    return newMap;
                });
            });
        }
    };

    // 新增：上传字幕文件到后端的函数
    const uploadSubtitleToBackend = async (files: File[]) => {
        const fileKeys = files.map(f => `subtitle-${f.name}-${f.size}`);
        
        // 检查是否已经在上传中
        const alreadyUploading = fileKeys.some(key => uploadingFiles.has(key));
        if (alreadyUploading) {
            console.log('字幕文件正在上传中，跳过重复上传');
            return Promise.resolve([]); // 返回空数组，避免undefined
        }

        // 添加上传状态
        setUploadingFiles(prev => {
            const newSet = new Set(prev);
            fileKeys.forEach(key => newSet.add(key));
            return newSet;
        });

        // 创建AbortController用于取消上传
        const abortController = new AbortController();
        fileKeys.forEach(key => {
            setUploadControllers(prev => new Map(prev).set(key, abortController));
        });

        try {
            // 从URL参数获取剧集ID和是否纯净版
            let currentFolderId = searchParams.get('seriesId');
            const isPure = searchParams.get('isPure') === 'true';
            
            if (!currentFolderId || currentFolderId === '0') {
                showError('请先选择剧集文件夹');
                throw new Error('请先选择剧集文件夹');
            }

            const seriesId = parseInt(currentFolderId!);
            
            // 新增：过滤掉没有对应视频的字幕文件（检查左侧上传视频文件列表）
            const validFiles: File[] = [];
            const invalidFiles: { file: File; name: string }[] = [];
            
            for (const file of files) {
                const subtitleNameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
                // 检查字幕文件名（去掉后缀）是否在左侧上传视频文件列表中
                const videoExists = videoFiles.some(video => {
                    const videoNameWithoutExt = video.name.replace(/\.[^/.]+$/, '');
                    return videoNameWithoutExt === subtitleNameWithoutExt;
                });
                
                if (videoExists) {
                    validFiles.push(file);
                } else {
                    invalidFiles.push({ file, name: subtitleNameWithoutExt });
                }
            }
            
            // 如果有无效的字幕文件，显示弹窗提示
            if (invalidFiles.length > 0) {
                // 先添加所有字幕文件到列表中（包括无效的）
                const newSubtitleFiles: SubtitleFile[] = files.map(file => ({
                    uid: `${Date.now()}-${Math.random()}-${file.name}`,
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    status: 'uploading' as const,
                    file: file
                }));
                setSubtitleFiles(prev => [...prev, ...newSubtitleFiles]);
                
                // 找到对应的字幕文件对象
                const invalidSubtitleFiles = newSubtitleFiles.filter(sf => {
                    const subtitleNameWithoutExt = sf.name.replace(/\.[^/.]+$/, '');
                    return invalidFiles.some(invalid => invalid.name === subtitleNameWithoutExt);
                });
                
                // 显示匹配不成功弹窗
                setSubtitleMatchInfo({
                    invalidFiles,
                    allFiles: files,
                    subtitleFiles: invalidSubtitleFiles
                });
                setSubtitleMatchModalVisible(true);
                
                // 抛出特殊错误，阻止继续上传
                throw new Error('SUBTITLE_MATCH_FAILED');
            }
            
            // 修复：字幕上传时使用字幕文件名作为项目名称
            const projectName = videoName.trim() || (validFiles.length > 1 ? '多字幕项目' : validFiles[0]?.name || '未命名项目');

            console.log('上传字幕文件到后端，参数：', { files: validFiles, seriesId, projectName });

            // 使用新的上传接口，逐个上传文件（只上传有效的文件）
            const newResourceIds: number[] = [];
            const newTaskIds: number[] = [];

            for (const file of validFiles) {
                try {
                    const taskId = await uploadSingleFileToTos(seriesId, file, 'subtitle');
                    newResourceIds.push(taskId);
                    newTaskIds.push(taskId);
                    console.log(`字幕文件 ${file.name} 上传成功，任务ID：`, taskId);
                } catch (error: any) {
                    console.error(`字幕文件 ${file.name} 上传失败:`, error);
                    throw new Error(`字幕文件 ${file.name} 上传失败: ${error.message}`);
                }
            }

            if (newResourceIds.length > 0) {
                setUploadedResourceIds(prev => [...prev, ...newResourceIds]);
                console.log('字幕文件上传到后端成功，资源ID：', newResourceIds);
                console.log('字幕响应数据中data的id列表：', newTaskIds);
                
                // 将新的task ID添加到全局taskIds数组中
                setTaskIds(prev => [...prev, ...newTaskIds]);
                console.log('更新后的全局taskIds数组：', [...taskIds, ...newTaskIds]);
                
                return newResourceIds; // 返回资源ID数组
            } else {
                throw new Error('没有文件上传成功');
            }
        } catch (error: any) {
            // 如果是匹配失败错误，不显示错误提示（已显示弹窗）
            if (error.message === 'SUBTITLE_MATCH_FAILED') {
                throw error;
            }
            console.error('字幕文件上传到后端失败:', error);
            showError(error.message || '字幕文件上传失败，请重试');
            throw error;
        } finally {
            // 移除上传状态
            setUploadingFiles(prev => {
                const newSet = new Set(prev);
                fileKeys.forEach(key => newSet.delete(key));
                return newSet;
            });
            
            // 清理AbortController
            fileKeys.forEach(key => {
                setUploadControllers(prev => {
                    const newMap = new Map(prev);
                    newMap.delete(key);
                    return newMap;
                });
            });
        }
    };

    // 新增：存储最近添加的剧本文件，用于取消时删除
    const [recentlyAddedScriptFile, setRecentlyAddedScriptFile] = useState<any>(null);

    // 新增：处理文件选择的函数
    const handleFileSelection = (files: File[], fileType: 'video' | 'script' | 'subtitle') => {
        // 文件类型验证
        const invalidFiles: File[] = [];
        if (fileType === 'video') {
            // 视频只支持视频格式
            const videoExtensions = ['.mp4', '.avi', '.mov', '.mkv', '.wmv', '.flv', '.webm', '.m4v'];
            files.forEach(file => {
                const fileName = file.name.toLowerCase();
                const isVideoFile = videoExtensions.some(ext => fileName.endsWith(ext)) || 
                                   file.type.startsWith('video/');
                if (!isVideoFile) {
                    invalidFiles.push(file);
                }
            });
        } else if (fileType === 'script') {
            // 剧本只支持 doc 和 docx
            files.forEach(file => {
                const fileName = file.name.toLowerCase();
                if (!fileName.endsWith('.doc') && !fileName.endsWith('.docx')) {
                    invalidFiles.push(file);
                }
            });
        } else if (fileType === 'subtitle') {
            // 字幕只支持 srt
            files.forEach(file => {
                const fileName = file.name.toLowerCase();
                if (!fileName.endsWith('.srt')) {
                    invalidFiles.push(file);
                }
            });
        }

        if (invalidFiles.length > 0) {
            let fileTypeName = '';
            let allowedFormats = '';
            if (fileType === 'video') {
                fileTypeName = '视频';
                allowedFormats = 'mp4、avi、mov、mkv、wmv、flv、webm、m4v 等视频格式';
            } else if (fileType === 'script') {
                fileTypeName = '剧本';
                allowedFormats = 'doc 和 docx';
            } else {
                fileTypeName = '字幕';
                allowedFormats = 'srt';
            }
            message.error(`${fileTypeName}文件只支持 ${allowedFormats} 格式，请重新选择文件`);
            return; // 阻止处理无效文件
        }

        // 检查是否有重复文件
        const duplicateFiles = files.filter(file => {
            const { isDuplicate } = checkDuplicateFile(file.name, fileType);
            return isDuplicate;
        });

        if (duplicateFiles.length > 0) {
            // 有重复文件，存储整个批次等待用户确认
            setPendingFileBatch({
                files,
                fileType,
                hasDuplicate: true,
                duplicateFileNames: duplicateFiles.map(f => f.name)
            });

            setDuplicateFileInfo({
                fileName: duplicateFiles[0].name,  // 这里只取了第一个重复文件名
                fileType,
                existingFile: checkDuplicateFile(duplicateFiles[0].name, fileType).existingFile,
                newFile: duplicateFiles[0],  // 这里只取了第一个重复文件
                allFiles: files
            });
            setDuplicateFileModalVisible(true);
        } else {
            // 没有重复文件，先存储到最近添加的文件中，等待用户确认
            setPendingFileBatch({
                files,
                fileType,
                hasDuplicate: false,
                duplicateFileNames: []
            });

            // 显示确认弹窗
            setDuplicateFileInfo({
                fileName: '',
                fileType,
                existingFile: null,
                newFile: files[0],
                allFiles: files
            });
            setDuplicateFileModalVisible(true);
        }
    };

    // 新增：语言友好化显示函数
    const getFriendlyLanguageName = (langCode: string): string => {
        const languageMap: { [key: string]: string } = {
            'zh': '中文',
            'en': '英语',
            'ja': '日语',
            'ko': '韩语',
        };

        // 如果找到对应的友好名称，返回它；否则返回原始代码
        return languageMap[langCode] || langCode;
    };

    // 获取剧集名称和目标语言
    useEffect(() => {
        const getSeriesInfo = async () => {
            try {
                // 从URL参数获取剧集ID
                let currentFolderId = searchParams.get('seriesId');

                if (currentFolderId && currentFolderId !== '0') {
                    // 调用API获取剧集列表
                    const response = await getSeriesListApi({
                        pageNo: 1,
                        pageSize: 1000 // 获取足够多的数据来找到对应的剧集
                    });

                    // 查找匹配的剧集
                    const targetSeries = (response.data?.list || []).find((series: any) => series.id === parseInt(currentFolderId!));

                    if (targetSeries) {
                        setSeriesName(targetSeries!.name);
                        // 从剧集信息中获取目标语言，并转换为友好显示
                        const friendlyLanguage = getFriendlyLanguageName(targetSeries!.target_lang || '');
                        setTargetLanguage(friendlyLanguage);
                        // 新增：从剧集信息中获取字幕擦除状态
                        setSubtitleErase(targetSeries!.need_removal_subtitle || false);
                    } else {
                        setSeriesName('未知剧集');
                        setTargetLanguage('日语'); // 默认值
                        setSubtitleErase(false); // 默认值
                    }
                } else {
                    setSeriesName('未选择剧集');
                    setTargetLanguage('日语'); // 默认值
                    setSubtitleErase(false); // 默认值
                }
            } catch (error: any) {
                console.error('获取剧集信息失败:', error);
                setSeriesName('获取失败');
                setTargetLanguage('日语'); // 默认值
                setSubtitleErase(false); // 默认值
            } finally {
                setIsLoadingSeries(false);
            }
        };

        const loadInitialData = async () => {
            // 优化：分步加载，先快速显示基本信息，再异步加载详细信息
            
            // 第一步：立即开始加载视频列表（优先级最高）
            const videoLoadPromise = fetchWaitingVideos().catch(error => {
                console.error('获取等待视频失败:', error);
                return [];
            });

            // 第二步：并行加载剧集信息
            const seriesLoadPromise = getSeriesInfo();

            // 第三步：优先处理视频数据，立即显示
            try {
                const waitingVideosList = await videoLoadPromise;
                
                if (waitingVideosList.length > 0) {
                    // 快速转换并显示视频列表
                    const convertedVideos = convertWaitingVideosToUploadFormat(waitingVideosList);
                    
                    // 立即更新UI，不等待剧集信息
                    setVideoFiles(convertedVideos);
                    
                    // 更新task_ids
                    const waitingVideoIds = waitingVideosList.map(video => video.id);
                    setTaskIds(waitingVideoIds);
                    
                    console.log('快速加载完成，视频ID：', waitingVideoIds);
                    
                    // 显示成功消息（延迟显示，避免干扰）
                    setTimeout(() => {
                        message.success(`已加载 ${waitingVideosList.length} 个等待处理的视频`);
                    }, 500);
                }
                
                // 视频加载完成后立即隐藏加载状态
                setIsLoadingVideos(false);
                
            } catch (error) {
                console.error('视频加载失败:', error);
                setIsLoadingVideos(false);
            }

            // 第四步：异步等待剧集信息加载完成（不阻塞视频显示）
            try {
                await seriesLoadPromise;
            } catch (error) {
                console.error('剧集信息加载失败:', error);
            }
        };

        loadInitialData();
    }, [searchParams]); // 添加 searchParams 依赖

    // 优化：组件挂载时立即开始预加载
    useEffect(() => {
        // 立即开始预加载，不等待其他逻辑
        preloadVideoData();
    }, [preloadVideoData]);

    // 优化：监听视频文件变化，重置虚拟化范围
    useEffect(() => {
        if (videoFiles.length > 0) {
            setVisibleRange({ start: 0, end: Math.min(11, videoFiles.length) });
        }
    }, [videoFiles.length]);

    // 新增：当视频列表有视频且没有选中任何视频时，自动预览第一个视频
    useEffect(() => {
        if (videoFiles.length > 0 && selectedVideoIndex === -1) {
            setSelectedVideoIndex(0);
        }
    }, [videoFiles.length, selectedVideoIndex]);


    // 计算视频名称输入框是否应该禁用
    const isVideoNameDisabled = videoFiles.length === 0 || showVideoName || videoFiles.length > 1; // 没有视频文件时禁用，或者显示视频名称时禁用，或者多个视频时禁用

    // 修改：计算视频名称显示值
    const getVideoNameDisplayValue = () => {
        if (videoFiles.length === 0) return '';
        if (showVideoName && selectedVideoIndex >= 0 && videoFiles[selectedVideoIndex]) {
            // 当显示视频名称时，显示选中的视频名称
            return videoFiles[selectedVideoIndex].name;
        }
        // 没有点击具体视频时，不显示任何值，让placeholder提示用户
        return '';
    };

    const handleBack = () => {
        navigate(-1);
    };

    // 修改：生成视频函数
    const handleGenerateVideo = async () => {
        // 检查是否有视频文件（页面加载时已经包含了等待状态的视频）
        const hasVideos = videoFiles.length > 0;
        
        if (!hasVideos) {
            showWarning('请先上传视频文件');
            message.error('请上传视频文件');
            return;
        }

        // 修复：多个视频时，如果用户没有输入名称，使用默认名称而不是报错
        if (videoFiles.length > 1 && !videoName.trim()) {
            // 设置默认名称
            setVideoName('多视频项目');
            // 继续执行，不阻止用户
        }

        // 直接执行生成逻辑，不再显示确认弹窗
        await handleConfirmGenerate();
    };

    // 新增：文件检查函数
    const checkFiles = (): { isValid: boolean; message: string } => {
        // 检查剧本文件
        // if (scriptFiles.length === 0) {
        //     return {
        //         isValid: false,
        //         message: '您的剧本文件上传不全,是否继续?\n注:缺少剧本文件生成视频可能会导致翻译配音效果不佳'
        //     };
        // }

        // 检查字幕文件
        if (subtitleFiles.length === 0) {
            return {
                isValid: false,
                message: '您的字幕文件上传不全,是否继续生成?\n注:缺少字幕文件生成视频可能会导致翻译配音效果不佳'
            };
        }

        // 修改：检查字幕文件是否与视频文件按名称匹配
        if (subtitleFiles.length > 0 && videoFiles.length > 0) {
            // 获取所有视频文件名（不含扩展名）
            const videoNames = videoFiles.map(vf => vf.name.replace(/\.[^/.]+$/, ""));
            // 获取所有字幕文件名（不含扩展名）
            const subtitleNames = subtitleFiles.map(sf => sf.name.replace(/\.[^/.]+$/, ""));

            // 找出缺少字幕文件的视频文件
            const missingSubtitles = videoNames.filter(videoName => !subtitleNames.includes(videoName));

            if (missingSubtitles.length > 0) {
                const missingList = missingSubtitles.join('、');
                return {
                    isValid: false,
                    message: `缺少${missingList}视频文件的字幕文件，是否继续生成?\n注:缺少字幕文件生成视频可能会导致翻译配音效果不佳`
                };
            }
        }

        return { isValid: true, message: '' };
    };

    // 新增：剧本文件名检查函数
    const checkScriptFileName = (scriptFileName: string): { isValid: boolean; message: string } => {
        const scriptNameWithoutExt = scriptFileName.replace(/\.[^/.]+$/, ""); // 移除文件扩展名

        if (scriptNameWithoutExt !== seriesName) {
            return {
                isValid: false,
                message: `您的剧本文件名称"${scriptNameWithoutExt}"与剧集名称"${seriesName}"不一致\n注:剧本文件名称与剧集名称不一致可能会导致翻译配音效果不佳`
            };
        }

        return { isValid: true, message: '' };
    };

    // 修改：确认生成视频函数（去掉弹窗，直接执行）
    const handleConfirmGenerate = async () => {
        // 直接执行生成逻辑，不再进行文件检查和显示提醒弹窗
        await executeGenerate();
    };

    // 修改：执行生成的函数，直接使用已上传的资源ID提交
    const executeGenerate = async () => {
        // 新增：在开始生成前，清理文件处理状态，避免冲突
        if (duplicateFileModalVisible || duplicateFileInfo) {
            setDuplicateFileModalVisible(false);
            setDuplicateFileInfo(null);
            setPendingFileBatch(null);
        }

        setIsGenerating(true);

        try {
            // 从URL参数获取剧集ID
            let currentFolderId = searchParams.get('seriesId');
            console.log('获取到的currentFolderId:', currentFolderId, '来源: URL参数');

            if (!currentFolderId || currentFolderId === '0') {
                message.error('请先选择剧集文件夹');
                setIsGenerating(false);
                return;
            }

            const seriesId = parseInt(currentFolderId!);
            console.log('解析后的seriesId:', seriesId, '是否有效:', !isNaN(seriesId) && seriesId > 0);
            const projectName = videoName.trim() || (videoFiles.length > 1 ? '多视频项目' : videoFiles[0]?.name || '未命名项目');

            // 检查是否还有文件正在上传中
            if (uploadingFiles.size > 0) {
                message.warning('还有文件正在上传中，请等待上传完成后再生成');
                setIsGenerating(false);
                return;
            }

            // 检查是否有可用的资源ID（包括已上传的和等待状态的）
            const allResourceIds = [...uploadedResourceIds, ...taskIds];
            if (allResourceIds.length === 0) {
                message.warning('没有可用的资源，请等待文件上传完成后再生成');
                setIsGenerating(false);
                return;
            }

            // 从URL参数获取是否纯净版
            const isPure = searchParams.get('isPure') === 'true';
            
            console.log('提交资源，参数：', { seriesId, isPure });
            
            // 将提示信息存储到localStorage，在页面切换后显示
            localStorage.setItem('videoGenerateAlert', JSON.stringify({
                type: 'success',
                message: '您的视频已开始在后台生成，请耐心等待',
                timestamp: Date.now()
            }));

            // 关闭生成弹窗
            setGenerateModalVisible(false);
            setIsGenerating(false);

            // 立即跳转到视频列表页，不等待请求完成
            if (seriesId && !isNaN(seriesId) && seriesId > 0) {
                // 如果seriesName是"获取失败"，则不传递seriesName参数，让视频列表页自己获取
                let videoListUrl: string;
                if (seriesName && seriesName !== '获取失败' && seriesName !== '未知剧集' && seriesName !== '未选择剧集') {
                    const encodedSeriesName = encodeURIComponent(seriesName);
                    videoListUrl = isPure 
                        ? `/videoList?seriesId=${seriesId}&seriesName=${encodedSeriesName}&isPure=true`
                        : `/videoList?seriesId=${seriesId}&seriesName=${encodedSeriesName}`;
                } else {
                    // 不传递seriesName，让视频列表页自己获取
                    videoListUrl = isPure 
                        ? `/videoList?seriesId=${seriesId}&isPure=true`
                        : `/videoList?seriesId=${seriesId}`;
                }
                navigate(videoListUrl);
            } else {
                console.error('seriesId无效，无法跳转:', seriesId);
                navigate('/videoList');
            }

            // 在后台异步执行生成请求
            submitResourceApi(seriesId)
                .then(response => {
                    if (response && response.code === 0) {
                        console.log('视频生成任务提交成功，任务ID:', response.data);
                    } else {
                        console.error('视频生成任务提交失败:', response?.msg || '资源提交失败');
                    }
                })
                .catch(error => {
                    console.error('生成失败:', error);
                });

        } catch (error: any) {
            console.error('生成失败:', error);
            message.error(error.message || '生成失败，请重试');
            setIsGenerating(false);
        }
    };

    // 新增：取消生成
    const handleCancelGenerate = () => {
        setGenerateModalVisible(false);
    };

    // 新增：提醒弹窗确认处理
    const handleAlertConfirm = async () => {
        console.log('提醒弹窗确认，当前状态：');
        console.log('uploadingFiles.size:', uploadingFiles.size);
        console.log('uploadedResourceIds:', uploadedResourceIds);
        setAlertModalVisible(false);
        await executeGenerate();
    };

    const handleAlertConfirmK = async () => {
        setAlertModalVisibleK(false);
        // await executeGenerate();
    };

    // 修改：提醒弹窗取消处理
    const handleAlertCancel = () => {
        setAlertModalVisible(false);
    };

    const handleAlertCancelK = () => {
        console.log('handleAlertCancelK 被调用');
        console.log('recentlyAddedScriptFile:', recentlyAddedScriptFile);

        setAlertModalVisibleK(false);

        // 删除最近上传的剧本文件
        if (recentlyAddedScriptFile) {
            console.log('准备删除剧本文件:', recentlyAddedScriptFile.name);

            // 从剧本文件列表中删除
            setScriptFiles(prev => {
                console.log('删除前的剧本文件列表:', prev);
                const filtered = prev.filter(file => file.uid !== recentlyAddedScriptFile.uid);
                console.log('删除后的剧本文件列表:', filtered);
                return filtered;
            });

            // 清理最近添加的剧本文件记录
            setRecentlyAddedScriptFile(null);

            message.info(`已删除剧本文件：${recentlyAddedScriptFile.name}`);
            console.log('剧本文件删除完成');
        } else {
            console.log('没有找到最近添加的剧本文件');
        }
    };

    // 优化：异步获取视频时长和生成缩略图，支持缓存和错误处理
    const getVideoInfo = (file: File): Promise<{ duration: number; thumbnail: string }> => {
        // 检查缓存
        const cacheKey = `${file.name}-${file.size}-${file.lastModified}`;
        const cachedThumbnail = thumbnailCache.current.get(cacheKey);
        
        if (cachedThumbnail) {
            console.log('使用缓存的缩略图:', file.name);
            return Promise.resolve({ duration: 0, thumbnail: cachedThumbnail });
        }
        
        return new Promise((resolve, reject) => {
            const video = document.createElement('video');
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            // 设置视频属性以优化性能
            video.muted = true;
            video.playsInline = true;
            video.preload = 'metadata';
            video.crossOrigin = 'anonymous';

            let isResolved = false;
            const cleanup = () => {
                if (video.src) {
                    URL.revokeObjectURL(video.src);
                }
                video.remove();
                canvas.remove();
            };

            const resolveOnce = (result: { duration: number; thumbnail: string }) => {
                if (!isResolved) {
                    isResolved = true;
                    cleanup();
                    resolve(result);
                }
            };

            const rejectOnce = (error: Error) => {
                if (!isResolved) {
                    isResolved = true;
                    cleanup();
                    reject(error);
                }
            };

            video.onloadedmetadata = () => {
                try {
                    const duration = Math.round(video.duration);

                    // 优化：使用更短的时间点生成缩略图，减少等待时间
                    video.currentTime = Math.min(1, duration * 0.1); // 使用10%位置或1秒，取较小值
                    
                    video.onseeked = () => {
                        try {
                            if (ctx && video.videoWidth > 0 && video.videoHeight > 0) {
                                // 获取视频的原始宽高比
                                const videoAspectRatio = video.videoWidth / video.videoHeight;

                                // 优化：使用更小的缩略图尺寸以提高性能
                                const maxWidth = 160;
                                const maxHeight = 120;

                                let canvasWidth, canvasHeight;

                                // 根据视频宽高比计算画布尺寸，保持原始比例
                                if (videoAspectRatio > maxWidth / maxHeight) {
                                    canvasWidth = maxWidth;
                                    canvasHeight = maxWidth / videoAspectRatio;
                                } else {
                                    canvasHeight = maxHeight;
                                    canvasWidth = maxHeight * videoAspectRatio;
                                }

                                // 设置画布尺寸
                                canvas.width = canvasWidth;
                                canvas.height = canvasHeight;

                                // 在画布中央绘制视频，保持原始比例
                                ctx.drawImage(video, 0, 0, canvasWidth, canvasHeight);

                                // 优化：使用更低的图片质量以提高性能
                                const thumbnail = canvas.toDataURL('image/jpeg', 0.6);
                                
                                // 缓存缩略图
                                thumbnailCache.current.set(cacheKey, thumbnail);
                                
                                resolveOnce({ duration, thumbnail });
                            } else {
                                rejectOnce(new Error('无法获取视频信息'));
                            }
                        } catch (error) {
                            rejectOnce(new Error(`生成缩略图失败: ${error}`));
                        }
                    };

                    video.onerror = () => {
                        rejectOnce(new Error('视频加载失败'));
                    };

                    // 设置超时处理
                    setTimeout(() => {
                        if (!isResolved) {
                            rejectOnce(new Error('视频信息获取超时'));
                        }
                    }, 10000); // 10秒超时

                } catch (error) {
                    rejectOnce(new Error(`处理视频元数据失败: ${error}`));
                }
            };

            video.onerror = () => {
                rejectOnce(new Error('视频加载失败'));
            };

            try {
                video.src = URL.createObjectURL(file);
            } catch (error) {
                rejectOnce(new Error(`创建视频URL失败: ${error}`));
            }
        });
    };

    // 格式化时长显示
    const formatDuration = (seconds: number): string => {
        if (isNaN(seconds)) return '00:00';
        
        // 计算分、秒，确保秒数取整
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        
        // 补0（不足2位补0，如1→01）
        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    // 新增：取消上传任务
    const cancelUpload = (fileKey: string) => {
        const controller = uploadControllers.get(fileKey);
        if (controller) {
            controller.abort();
            console.log('取消上传任务:', fileKey);
            
            // 立即清理状态
            setUploadingFiles(prev => {
                const newSet = new Set(prev);
                newSet.delete(fileKey);
                return newSet;
            });
            
            setUploadControllers(prev => {
                const newMap = new Map(prev);
                newMap.delete(fileKey);
                return newMap;
            });
        }
    };

    // 新增：删除已上传的任务
    const deleteUploadedTask = async (resourceId: number) => {
        try {
            console.log('=== 开始删除任务 ===');
            console.log('调用 delTaskApi，task_id:', resourceId);
            console.log('请求URL: /task/delete/' + resourceId);
            console.log('请求方法: DELETE');
            
            const result = await delTaskApi(resourceId);
            
            console.log('删除接口调用成功，返回结果:', result);
            console.log('=== 删除任务完成 ===');
            message.success('任务删除成功');
        } catch (error) {
            console.error('=== 删除任务失败 ===');
            console.error('错误详情:', error);
            console.error('resourceId:', resourceId);
            message.error('删除任务失败，请重试');
            throw error; // 重新抛出错误，让调用方知道删除失败
        }
    };

    // 修改：删除单个视频文件
    const handleRemoveVideo = async (uid: string) => {
        const videoFile = videoFiles.find(file => file.uid === uid);
        if (!videoFile) {
            console.log('未找到要删除的视频文件，uid:', uid);
            return;
        }

        // 清理本地预览 URL，避免内存泄漏
        if (videoFile.previewUrl) {
            URL.revokeObjectURL(videoFile.previewUrl);
        }

        console.log('开始删除视频文件:', {
            name: videoFile.name,
            status: videoFile.status,
            resourceId: videoFile.resourceId,
            uid: videoFile.uid
        });
        
        // 检查文件状态和resourceId
        console.log('文件状态检查:', {
            isUploading: videoFile.status === 'uploading',
            isDone: videoFile.status === 'done',
            hasResourceId: !!videoFile.resourceId,
            resourceIdValue: videoFile.resourceId
        });

        try {
            // 如果正在上传，取消上传任务
            if (videoFile.status === 'uploading') {
                const fileKey = `video-${videoFile.name}-${videoFile.size}`;
                cancelUpload(fileKey);
                message.info('已取消视频上传任务');
            }
            // 如果上传完成，删除已上传的任务
            else if (videoFile.status === 'done') {
                // 使用视频文件自身的 resourceId，避免异步导致删除时传错 id
                if (videoFile.resourceId) {
                    const taskId = videoFile.resourceId;
                    console.log('删除已上传的视频任务，task_id:', taskId);
                    console.log('准备调用删除接口 delTaskApi，参数:', taskId);
                    console.log('当前视频文件的 resourceId:', videoFile.resourceId);

                    await deleteUploadedTask(taskId);

                    setTaskIds(prev => {
                        const newTaskIds = prev.filter(id => id !== taskId);
                        console.log('删除成功，更新后的 taskIds 数组:', newTaskIds);
                        return newTaskIds;
                    });

                    setUploadedResourceIds(prev => {
                        const newResourceIds = prev.filter(id => id !== taskId);
                        console.log('删除成功，更新后的 uploadedResourceIds 数组:', newResourceIds);
                        return newResourceIds;
                    });

                    console.log('删除接口调用完成');
                } else {
                    console.log('视频文件没有 resourceId，跳过删除接口调用');
                    console.log('当前视频文件:', videoFile);
                }
            } else {
                console.log('跳过删除接口调用，原因:', {
                    status: videoFile.status,
                    hasResourceId: !!videoFile.resourceId,
                    resourceId: videoFile.resourceId
                });
            }

            // 删除本地文件（只有在删除操作成功后才执行）
            setVideoFiles(prev => prev.filter(file => file.uid !== uid));
            console.log('已从本地文件列表中删除视频文件');
            
            // 如果删除的是当前选中的视频，重置选中状态
            if (selectedVideoIndex >= 0 && videoFiles[selectedVideoIndex]?.uid === uid) {
                setSelectedVideoIndex(-1);
                setShowVideoName(false); // 删除选中视频时隐藏名称
                console.log('已重置视频选中状态');
            }
        } catch (error) {
            console.error('删除视频文件失败:', error);
            message.error('删除视频文件失败，请重试');
        }
    };

    // 新增：删除剧本文件
    const handleRemoveScript = async (uid: string) => {
        const scriptFile = scriptFiles.find(file => file.uid === uid);
        if (!scriptFile) return;

        // 如果正在上传，取消上传任务
        if (scriptFile.status === 'uploading') {
            const fileKey = `script-${scriptFile.name}-${scriptFile.size}`;
            cancelUpload(fileKey);
            message.info('已取消剧本上传任务');
        }
        // 如果上传完成，删除已上传的任务
        else if (scriptFile.status === 'done') {
            // 使用剧本文件自身的 resourceId，避免异步导致删除时传错 id
            if (scriptFile.resourceId) {
                const taskId = scriptFile.resourceId;
                console.log('删除已上传的剧本任务，task_id:', taskId);
                console.log('当前剧本文件的 resourceId:', scriptFile.resourceId);

                await deleteUploadedTask(taskId);

                setTaskIds(prev => {
                    const newTaskIds = prev.filter(id => id !== taskId);
                    console.log('剧本删除成功，更新后的 taskIds 数组:', newTaskIds);
                    return newTaskIds;
                });

                setUploadedResourceIds(prev => {
                    const newResourceIds = prev.filter(id => id !== taskId);
                    console.log('剧本删除成功，更新后的 uploadedResourceIds 数组:', newResourceIds);
                    return newResourceIds;
                });
            } else {
                console.log('剧本文件没有 resourceId，跳过删除接口调用');
            }
        }

        // 删除本地文件
        setScriptFiles(prev => prev.filter(file => file.uid !== uid));
    };

    // 新增：删除字幕文件
    const handleRemoveSubtitle = async (uid: string) => {
        const subtitleFile = subtitleFiles.find(file => file.uid === uid);
        if (!subtitleFile) return;

        // 如果正在上传，取消上传任务
        if (subtitleFile.status === 'uploading') {
            const fileKey = `subtitle-${subtitleFile.name}-${subtitleFile.size}`;
            cancelUpload(fileKey);
            message.info('已取消字幕上传任务');
        }
        // 如果上传完成，删除已上传的任务
        else if (subtitleFile.status === 'done') {
            // 使用字幕文件自身的 resourceId，避免异步导致删除时传错 id
            if (subtitleFile.resourceId) {
                const taskId = subtitleFile.resourceId;
                console.log('删除已上传的字幕任务，task_id:', taskId);
                console.log('当前字幕文件的 resourceId:', subtitleFile.resourceId);

                await deleteUploadedTask(taskId);

                setTaskIds(prev => {
                    const newTaskIds = prev.filter(id => id !== taskId);
                    console.log('字幕删除成功，更新后的 taskIds 数组:', newTaskIds);
                    return newTaskIds;
                });

                setUploadedResourceIds(prev => {
                    const newResourceIds = prev.filter(id => id !== taskId);
                    console.log('字幕删除成功，更新后的 uploadedResourceIds 数组:', newResourceIds);
                    return newResourceIds;
                });
            } else {
                console.log('字幕文件没有 resourceId，跳过删除接口调用');
            }
        }

        // 删除本地文件
        setSubtitleFiles(prev => prev.filter(file => file.uid !== uid));
    };

    // 选择视频播放
    const handleVideoSelect = (index: number) => {
        setSelectedVideoIndex(index);
        // 新增：当选择视频时，自动设置视频名称并显示
        if (videoFiles[index]) {
            setVideoName(videoFiles[index].name);
            setShowVideoName(true);
        }
    };

    // 新增：检查文件是否重复的函数
    const checkDuplicateFile = (fileName: string, fileType: 'video' | 'script' | 'subtitle'): { isDuplicate: boolean; existingFile: any } => {
        let existingFiles: any[] = [];

        switch (fileType) {
            case 'video':
                existingFiles = videoFiles;
                break;
            case 'script':
                existingFiles = scriptFiles;
                break;
            case 'subtitle':
                existingFiles = subtitleFiles;
                break;
        }

        const existingFile = existingFiles.find(file => file.name === fileName);
        return {
            isDuplicate: !!existingFile,
            existingFile
        };
    };

    // 新增：检查视频是否在剧集中已存在的函数
    const checkVideoExistsInSeries = async (videoName: string, seriesId: number): Promise<boolean> => {
        try {
            // 这里语言编码暂时使用 'en'，用于统计等待中的视频数量
            const taskList = await getTaskListApi({ seriesId, langCode: 'en' });
            const existingVideo = taskList.data.find(task => task.name === videoName);
            return !!existingVideo;
        } catch (error) {
            console.error('检查视频是否在剧集中存在时出错:', error);
            // 如果检查失败，为了安全起见，允许上传
            return false;
        }
    };



    // 新增：处理重复文件的函数
    const handleDuplicateFile = (fileName: string, fileType: 'video' | 'script' | 'subtitle', newFile: File, allFiles: File[]) => {
        const { isDuplicate, existingFile } = checkDuplicateFile(fileName, fileType);

        if (isDuplicate) {
            // 存储当前批次的所有文件
            setCurrentBatchFiles(prev => ({
                ...prev,
                [fileType]: allFiles
            }));

            setDuplicateFileInfo({
                fileName,
                fileType,
                existingFile,
                newFile,
                allFiles // 添加所有文件信息
            });
            setDuplicateFileModalVisible(true);
            return false; // 阻止上传
        }

        return true; // 允许上传
    };

    // 修改：确认替换文件的函数，优化处理逻辑
    const handleConfirmReplace = async () => {
        if (!pendingFileBatch || !duplicateFileInfo) return;

        const { files, fileType, hasDuplicate, duplicateFileNames } = pendingFileBatch;

        if (hasDuplicate) {
            // 有重复文件，处理替换逻辑
            let replacedCount = 0;
            let uploadedCount = 0;

            // 先删除之前上传的重复文件
            duplicateFileNames.forEach(fileName => {
                switch (fileType) {
                    case 'video':
                        setVideoFiles(prev => prev.filter(file => file.name !== fileName));
                        break;
                    case 'script':
                        setScriptFiles(prev => prev.filter(file => file.name !== fileName));
                        break;
                    case 'subtitle':
                        setSubtitleFiles(prev => prev.filter(file => file.name !== fileName));
                        break;
                }
                replacedCount++;
            });

            // 然后添加现在的所有文件（包括重复的），绕过重复检查
            for (const file of files) {
                // 直接处理文件上传，不进行重复检查
                switch (fileType) {
                    case 'video':
                        handleVideoFileUpload(file).catch(error => {
                            console.error('视频文件上传失败:', error);
                        });
                        break;
                    case 'script':
                        handleScriptFileUpload(file).catch(error => {
                            console.error('剧本文件上传失败:', error);
                        });
                        break;
                    case 'subtitle':
                        handleSubtitleFileUpload(file).catch(error => {
                            console.error('字幕文件上传失败:', error);
                        });
                        break;
                }
                uploadedCount++;
            }

            // 显示详细的处理结果
            let messageText = '';
            if (replacedCount > 0 && uploadedCount > 0) {
                messageText = `成功替换 ${replacedCount} 个重复文件，上传 ${uploadedCount} 个新文件`;
            } else if (replacedCount > 0) {
                messageText = `成功替换 ${replacedCount} 个重复文件`;
            } else if (uploadedCount > 0) {
                messageText = `成功上传 ${uploadedCount} 个新文件`;
            }

            if (messageText) {
                message.success(messageText);
            }
        } else {
            // 没有重复文件，直接上传所有文件
            for (const file of files) {
                handleFileUpload(file, fileType).catch(error => {
                    console.error('文件上传失败:', error);
                });
            }
            message.success(`成功上传 ${files.length} 个文件`);
        }

        // 清理状态
        setPendingFileBatch(null);
        setDuplicateFileModalVisible(false);
        setDuplicateFileInfo(null);

        // 移除：不再自动弹出文件选择器
        // if (hasDuplicate) {
        //     setTimeout(() => {
        //         openFileSelector(fileType);
        //     }, 500);
        // }
    };

    // 优化：处理视频文件上传的辅助函数，支持异步处理和错误恢复
    const handleVideoFileUpload = async (file: File) => {
        // 验证文件格式：只支持视频格式
        const videoExtensions = ['.mp4', '.avi', '.mov', '.mkv', '.wmv', '.flv', '.webm', '.m4v'];
        const fileName = file.name.toLowerCase();
        const isVideoFile = videoExtensions.some(ext => fileName.endsWith(ext)) || 
                           file.type.startsWith('video/');
        if (!isVideoFile) {
            message.error('视频文件只支持 mp4、avi、mov、mkv、wmv、flv、webm、m4v 等视频格式，请重新选择文件');
            return;
        }

        // 立即创建视频文件对象，显示加载状态
        const previewUrl = URL.createObjectURL(file);

        const newVideoFile: VideoFile = {
            uid: `${Date.now()}-${Math.random()}`,
            name: file.name,
            size: file.size,
            type: file.type,
            status: 'uploading', // 初始状态设为上传中
            file: file,
            previewUrl
        };
        
        setVideoFiles(prev => [...prev, newVideoFile]);
        
        // 异步处理视频信息，解码成功才上传
        const processVideoAsync = async () => {
            const [videoInfo] = await Promise.allSettled([
                getVideoInfo(file).catch(error => {
                    console.warn(`获取视频信息失败: ${error.message}`);
                    return Promise.reject(error);
                })
            ]);

            const decodeFailed = videoInfo.status !== 'fulfilled';
            const info = videoInfo.status === 'fulfilled' ? videoInfo.value : { duration: 0, thumbnail: '' };

            setVideoFiles(prev => prev.map(vf =>
                vf.uid === newVideoFile.uid
                    ? {
                        ...vf,
                        duration: info.duration,
                        thumbnail: info.thumbnail,
                        decodeFailed: decodeFailed ? true : vf.decodeFailed,
                        ...(decodeFailed ? { status: 'done' as const } : {})
                    }
                    : vf
            ));

            if (decodeFailed) {
                return;
            }

            try {
                const resourceIds = await uploadVideoToBackend([file]);
                console.log('视频上传成功，返回的resourceIds:', resourceIds);
                setVideoFiles(prev => prev.map(vf =>
                    vf.uid === newVideoFile.uid
                        ? { ...vf, status: 'done' as const, resourceId: resourceIds?.[0] }
                        : vf
                ));
                message.success(`${file.name} 上传成功`);
                checkMissingSubtitleAfterVideoUpload();
            } catch (error) {
                setVideoFiles(prev => prev.map(vf =>
                    vf.uid === newVideoFile.uid ? { ...vf, status: 'error' as const } : vf
                ));
                console.error('视频文件上传失败:', error);
                const reason = (error && (error as any).message) || '';
                const msg = reason
                    ? `视频文件 ${file.name} 上传失败：${reason}`
                    : `视频文件 ${file.name} 上传失败`;
                showError(msg);
            }
        };

        // 使用 setTimeout 确保不阻塞UI渲染
        setTimeout(processVideoAsync, 0);
    };

    // 新增：处理剧本文件上传的辅助函数（绕过重复检查）
    const handleScriptFileUpload = async (file: File) => {
        console.log('handleScriptFileUpload 被调用，文件:', file.name);

        // 验证文件格式：只支持 doc 和 docx
        const fileName = file.name.toLowerCase();
        if (!fileName.endsWith('.doc') && !fileName.endsWith('.docx')) {
            message.error('剧本文件只支持 doc 和 docx 格式，请重新选择文件');
            return;
        }

        const newScriptFile: ScriptFile = {
            uid: `${Date.now()}-${Math.random()}`,
            name: file.name,
            size: file.size,
            type: file.type,
            status: 'uploading' as const, // 初始状态设为上传中
            file: file
        };
        setScriptFiles(prev => [...prev, newScriptFile]);

        // 记录最近添加的剧本文件，用于取消时删除
        setRecentlyAddedScriptFile(newScriptFile);
        console.log('设置 recentlyAddedScriptFile:', newScriptFile);

        // 新增：立即上传剧本文件到后端（异步执行，不阻塞UI）
        uploadScriptToBackend([file])
            .then((resourceIds) => {
                // 上传成功，更新状态并保存资源ID
                setScriptFiles(prev => prev.map(sf => 
                    sf.uid === newScriptFile.uid ? { 
                        ...sf, 
                        status: 'done' as const,
                        resourceId: resourceIds?.[0] // 保存第一个资源ID
                    } : sf
                ));
                message.success(`${file.name} 上传成功`);
            })
            .catch(error => {
                // 上传失败，更新状态
                setScriptFiles(prev => prev.map(sf => 
                    sf.uid === newScriptFile.uid ? { 
                        ...sf, 
                        status: 'error' as const
                    } : sf
                ));
                console.error('剧本文件上传失败:', error);
                message.error(`${file.name} 上传失败`);
            });

        // 新增：剧本文件上传成功后立即检查文件名
        if (seriesName && seriesName !== '未知剧集' && seriesName !== '未选择剧集' && seriesName !== '获取失败') {
            const scriptCheck = checkScriptFileName(file.name);
            if (!scriptCheck.isValid) {
                console.log('剧本文件名检查失败，显示弹窗');
                // 显示剧本文件名提醒弹窗
                setAlertTitle('提醒');
                setAlertMessage(scriptCheck.message);
                setAlertModalVisibleK(true); // 改为使用 alertModalVisibleK
            }
        }
    };

    // 新增：处理字幕文件上传的辅助函数（绕过重复检查）
    const handleSubtitleFileUpload = async (file: File) => {
        // 验证文件格式：只支持 srt
        const fileName = file.name.toLowerCase();
        if (!fileName.endsWith('.srt')) {
            showError('字幕文件只支持 srt 格式，请重新选择文件');
            return;
        }

        // 新增：检查字幕文件对应的视频是否在左侧上传视频文件列表中（等待状态）
        // 获取字幕文件名（去掉后缀）
        const subtitleNameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        
        // 检查字幕文件名（去掉后缀）是否在左侧上传视频文件列表中
        const videoExists = videoFiles.some(video => {
            const videoNameWithoutExt = video.name.replace(/\.[^/.]+$/, '');
            return videoNameWithoutExt === subtitleNameWithoutExt;
        });
        
        if (!videoExists) {
            // 视频不存在，显示弹窗提示
            const newSubtitleFile: SubtitleFile = {
                uid: `${Date.now()}-${Math.random()}`,
                name: file.name,
                size: file.size,
                type: file.type,
                status: 'uploading' as const,
                file: file
            };
            setSubtitleFiles(prev => [...prev, newSubtitleFile]);
            
            // 显示匹配不成功弹窗
            setSubtitleMatchInfo({
                invalidFiles: [{ file, name: subtitleNameWithoutExt }],
                allFiles: [file],
                subtitleFiles: [newSubtitleFile]
            });
            setSubtitleMatchModalVisible(true);
            return;
        }

        const newSubtitleFile: SubtitleFile = {
            uid: `${Date.now()}-${Math.random()}`,
            name: file.name,
            size: file.size,
            type: file.type,
            status: 'uploading' as const, // 初始状态设为上传中
            file: file
        };
        setSubtitleFiles(prev => [...prev, newSubtitleFile]);
        
        // 新增：立即上传字幕文件到后端（异步执行，不阻塞UI）
        uploadSubtitleToBackend([file])
            .then((resourceIds) => {
                // 上传成功，更新状态并保存资源ID
                setSubtitleFiles(prev => prev.map(sf => 
                    sf.uid === newSubtitleFile.uid ? { 
                        ...sf, 
                        status: 'done' as const,
                        resourceId: resourceIds?.[0] // 保存第一个资源ID
                    } : sf
                ));
                message.success(`${file.name} 上传成功`, 10);
            })
            .catch(error => {
                // 上传失败（code 非 0），从列表中移除该文件
                setSubtitleFiles(prev => prev.filter(sf => sf.uid !== newSubtitleFile.uid));
                console.error('字幕文件上传失败:', error);
                message.error(`${file.name} 上传失败`);
            });

        // 修改：字幕文件上传成功后立即检查是否还有缺少字幕文件的视频
        checkMissingSubtitleAfterVideoUpload();
    };

    // 新增：处理字幕匹配不成功弹窗的确认（只移除没有匹配的字幕文件，其他还上传）
    const handleSubtitleMatchConfirm = async () => {
        if (!subtitleMatchInfo) return;
        
        const { invalidFiles, allFiles } = subtitleMatchInfo;
        
        // 从字幕文件列表中移除无效的文件
        setSubtitleFiles(prev => prev.filter(sf => {
            const subtitleNameWithoutExt = sf.name.replace(/\.[^/.]+$/, '');
            return !invalidFiles.some(invalid => invalid.name === subtitleNameWithoutExt);
        }));
        
        // 获取有效的文件（有匹配视频的文件）
        const validFiles = allFiles.filter(file => {
            const subtitleNameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
            return !invalidFiles.some(invalid => invalid.name === subtitleNameWithoutExt);
        });
        
        // 关闭弹窗
        setSubtitleMatchModalVisible(false);
        setSubtitleMatchInfo(null);
        
        // 如果有有效文件，继续上传
        if (validFiles.length > 0) {
            try {
                await uploadSubtitleToBackend(validFiles);
            } catch (error) {
                // 上传失败（code 非 0），从列表中移除所有上传失败的文件
                setSubtitleFiles(prev => prev.filter(sf => {
                    const subtitleNameWithoutExt = sf.name.replace(/\.[^/.]+$/, '');
                    return !validFiles.some(validFile => {
                        const validNameWithoutExt = validFile.name.replace(/\.[^/.]+$/, '');
                        return validNameWithoutExt === subtitleNameWithoutExt;
                    });
                }));
                console.error('字幕文件上传失败:', error);
                message.error('字幕文件上传失败');
            }
        }
    };

    // 新增：处理字幕匹配不成功弹窗的取消（取消本次所有选中的字幕都不上传）
    const handleSubtitleMatchCancel = () => {
        if (!subtitleMatchInfo) return;
        
        const { allFiles } = subtitleMatchInfo;
        
        // 从字幕文件列表中移除所有本次选中的字幕文件
        setSubtitleFiles(prev => prev.filter(sf => {
            return !allFiles.some(file => file.name === sf.name);
        }));
        
        // 关闭弹窗
        setSubtitleMatchModalVisible(false);
        setSubtitleMatchInfo(null);
    };

    // 修改：取消替换文件的函数
    const handleCancelReplace = () => {
        if (!pendingFileBatch) return;

        const { files, fileType, duplicateFileNames } = pendingFileBatch;

        // 只删除非重复文件，保留重复文件（之前上传的）
        const nonDuplicateFiles = files.filter(file => !duplicateFileNames.includes(file.name));

        if (nonDuplicateFiles.length > 0) {
            // 删除最近添加的非重复文件
            switch (fileType) {
                case 'video':
                    // 从视频文件列表中删除最近添加的非重复文件
                    setVideoFiles(prev => {
                        const fileNames = nonDuplicateFiles.map(f => f.name);
                        return prev.filter(file => !fileNames.includes(file.name));
                    });
                    break;

                case 'script':
                    // 从剧本文件列表中删除最近添加的非重复文件
                    setScriptFiles(prev => {
                        const fileNames = nonDuplicateFiles.map(f => f.name);
                        return prev.filter(file => !fileNames.includes(file.name));
                    });
                    break;

                case 'subtitle':
                    // 从字幕文件列表中删除最近添加的非重复文件
                    setSubtitleFiles(prev => {
                        const fileNames = nonDuplicateFiles.map(f => f.name);
                        return prev.filter(file => !fileNames.includes(file.name));
                    });
                    break;
            }

            // 显示取消消息
            message.info(`已取消文件上传，删除了 ${nonDuplicateFiles.length} 个非重复文件`);
        } else {
            // 没有非重复文件需要删除
            message.info('已取消文件上传');
        }

        // 清理状态
        setPendingFileBatch(null);
        setDuplicateFileModalVisible(false);
        setDuplicateFileInfo(null);
    };

    // 新增：打开文件选择器的函数
    const openFileSelector = (fileType: 'video' | 'script' | 'subtitle') => {
        // 创建隐藏的文件输入元素
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = true;

        // 根据文件类型设置接受的文件格式
        switch (fileType) {
            case 'video':
                input.accept = 'video/*,.mp4,.avi,.mov,.mkv,.wmv,.flv,.webm,.m4v';
                break;
            case 'script':
                input.accept = '.doc,.docx';
                break;
            case 'subtitle':
                input.accept = '.srt';
                break;
        }

        // 监听文件选择
        input.onchange = async (event) => {
            const files = (event.target as HTMLInputElement).files;
            if (files && files.length > 0) {
                // 处理选择的文件，在替换模式下绕过重复检查
                for (const file of Array.from(files)) {
                    // 直接调用辅助函数，不进行重复检查
                    switch (fileType) {
                        case 'video':
                            handleVideoFileUpload(file).catch(error => {
                                console.error('视频文件上传失败:', error);
                            });
                            break;
                        case 'script':
                            handleScriptFileUpload(file).catch(error => {
                                console.error('剧本文件上传失败:', error);
                            });
                            break;
                        case 'subtitle':
                            handleSubtitleFileUpload(file).catch(error => {
                                console.error('字幕文件上传失败:', error);
                            });
                            break;
                    }
                }
            }
        };

        // 触发文件选择
        input.click();

        // 清理
        setTimeout(() => {
            if (document.body.contains(input)) {
                document.body.removeChild(input);
            }
        }, 1000);
    };

    // 修改：处理文件上传的统一函数
    const handleFileUpload = async (file: File, fileType: 'video' | 'script' | 'subtitle') => {
        // 检查重复文件
        if (!handleDuplicateFile(file.name, fileType, file, [file])) {
            return; // 如果重复，会显示弹窗，这里直接返回
        }

        // 根据文件类型处理上传
        switch (fileType) {
            case 'video': {
                // 新增：检查视频是否在剧集中已存在
                let currentFolderId = searchParams.get('seriesId');
                if (currentFolderId && currentFolderId !== '0') {
                    try {
                        const seriesId = parseInt(currentFolderId);
                        const isVideoExistsInSeries = await checkVideoExistsInSeries(file.name, seriesId);
                        if (isVideoExistsInSeries) {
                            showError(`视频 "${file.name}" 在剧集中已存在，不允许重复上传`);
                            return;
                        }
                    } catch (e) {
                        // 检查失败不阻止添加
                    }
                }

                const previewUrl = URL.createObjectURL(file);
                const newVideoFile: VideoFile = {
                    uid: `${Date.now()}-${Math.random()}`,
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    status: 'uploading',
                    duration: 0,
                    thumbnail: '',
                    file: file,
                    previewUrl
                };
                setVideoFiles(prev => [...prev, newVideoFile]);
                checkMissingSubtitleAfterVideoUpload();

                getVideoInfo(file)
                    .then((videoInfo) => {
                        setVideoFiles(prev => prev.map(vf =>
                            vf.uid === newVideoFile.uid
                                ? { ...vf, duration: videoInfo.duration, thumbnail: videoInfo.thumbnail }
                                : vf
                        ));
                        uploadVideoToBackend([file])
                            .then(() => {
                                setVideoFiles(prev => prev.map(vf =>
                                    vf.uid === newVideoFile.uid ? { ...vf, status: 'done' as const } : vf
                                ));
                                message.success(`${file.name} 上传成功`);
                            })
                            .catch(error => {
                                setVideoFiles(prev => prev.map(vf =>
                                    vf.uid === newVideoFile.uid ? { ...vf, status: 'error' as const } : vf
                                ));
                                console.error('视频文件上传失败:', error);
                                message.error(`${file.name} 上传失败`);
                            });
                    })
                    .catch(() => {
                        setVideoFiles(prev => prev.map(vf =>
                            vf.uid === newVideoFile.uid
                                ? { ...vf, decodeFailed: true, status: 'done' as const }
                                : vf
                        ));
                        message.warning(`该视频无法在本机解码（可能为 H.265 等编码），已加入列表但不进行上传`);
                    });
                break;
            }

            case 'script':
                const newScriptFile = {
                    uid: `${Date.now()}-${Math.random()}`,
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    status: 'done' as const,
                    file: file
                };
                setScriptFiles(prev => [...prev, newScriptFile]);

                // 新增：记录最近添加的剧本文件，用于取消时删除
                setRecentlyAddedScriptFile(newScriptFile);
                console.log('handleFileUpload - 设置 recentlyAddedScriptFile:', newScriptFile);

                // 新增：立即上传剧本文件到后端（异步执行，不阻塞UI）
                uploadScriptToBackend([file]).catch(error => {
                    console.error('剧本文件上传失败:', error);
                });

                message.success(`${file.name} 上传成功`);

                // 新增：剧本文件上传成功后立即检查文件名
                if (seriesName && seriesName !== '未知剧集' && seriesName !== '未选择剧集' && seriesName !== '获取失败') {
                    const scriptCheck = checkScriptFileName(file.name);
                    if (!scriptCheck.isValid) {
                        // 显示剧本文件名提醒弹窗
                        setAlertTitle('提醒');
                        setAlertMessage(scriptCheck.message);
                        setAlertModalVisibleK(true);
                    }
                }
                break;

            case 'subtitle':
                const newSubtitleFile = {
                    uid: `${Date.now()}-${Math.random()}`,
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    status: 'uploading' as const,
                    file: file
                };
                setSubtitleFiles(prev => [...prev, newSubtitleFile]);
                
                // 新增：立即上传字幕文件到后端（异步执行，不阻塞UI）
                uploadSubtitleToBackend([file])
                    .then((resourceIds) => {
                        // 上传成功，更新状态并保存资源ID
                        setSubtitleFiles(prev => prev.map(sf => 
                            sf.uid === newSubtitleFile.uid ? { 
                                ...sf, 
                                status: 'done' as const,
                                resourceId: resourceIds?.[0]
                            } : sf
                        ));
                        message.success(`${file.name} 上传成功`, 5);
                    })
                    .catch(error => {
                        // 上传失败（code 非 0），从列表中移除该文件
                        setSubtitleFiles(prev => prev.filter(sf => sf.uid !== newSubtitleFile.uid));
                        console.error('字幕文件上传失败:', error);
                        message.error(`${file.name} 上传失败`);
                    });

                // 修改：字幕文件上传成功后立即检查是否还有缺少字幕文件的视频
                checkMissingSubtitleAfterVideoUpload();
                break;
        }
    };

    // 修改：视频文件上传后检查缺少字幕文件的函数
    const checkMissingSubtitleAfterVideoUpload = () => {
        // 移除 uploadSubtitle 检查，只要有视频文件就检查字幕
        const missingVideos = getMissingSubtitleVideos();
        if (missingVideos.length > 0) {
            const missingList = missingVideos.join('、');
            // showWarning(`检测到缺少字幕文件的视频：${missingList}`);
        }
    };

    /** 失败视频重传：调用与首次上传相同的对象存储上传流程（定义在 checkMissingSubtitleAfterVideoUpload 之后，避免白屏） */
    const handleRetryVideoUpload = useCallback(async (videoFile: VideoFile) => {
        if (!videoFile.file) {
            showError('无法重传：文件不存在，请重新选择视频');
            return;
        }
        const file = videoFile.file;
        setVideoFiles(prev => prev.map(vf =>
            vf.uid === videoFile.uid ? { ...vf, status: 'uploading' as const } : vf
        ));
        try {
            const resourceIds = await uploadVideoToBackend([file]);
            setVideoFiles(prev => prev.map(vf =>
                vf.uid === videoFile.uid
                    ? { ...vf, status: 'done' as const, resourceId: resourceIds?.[0] }
                    : vf
            ));
            message.success(`${file.name} 重传成功`);
            checkMissingSubtitleAfterVideoUpload();
        } catch (error) {
            setVideoFiles(prev => prev.map(vf =>
                vf.uid === videoFile.uid ? { ...vf, status: 'error' as const } : vf
            ));
            const reason = (error && (error as any).message) || '';
            showError(reason ? `重传失败：${reason}` : '视频重传失败，请重试');
        }
    }, [checkMissingSubtitleAfterVideoUpload]);

    // 修改：替换同名文件的函数，支持多种文件类型
    const replaceExistingFile = async (newFile: File, fileType: 'video' | 'script' | 'subtitle') => {
        const fileName = newFile.name;

        switch (fileType) {
            case 'video': {
                setVideoFiles(prev => prev.filter(f => f.name !== fileName));

                const previewUrl = URL.createObjectURL(newFile);
                const newVideoFile: VideoFile = {
                    uid: `${Date.now()}-${Math.random()}`,
                    name: newFile.name,
                    size: newFile.size,
                    type: newFile.type,
                    status: 'uploading',
                    duration: 0,
                    thumbnail: '',
                    file: newFile,
                    previewUrl
                };
                setVideoFiles(prev => [...prev, newVideoFile]);

                getVideoInfo(newFile)
                    .then((videoInfo) => {
                        setVideoFiles(prev => prev.map(vf =>
                            vf.uid === newVideoFile.uid
                                ? { ...vf, duration: videoInfo.duration, thumbnail: videoInfo.thumbnail }
                                : vf
                        ));
                        uploadVideoToBackend([newFile])
                            .then(() => {
                                setVideoFiles(prev => prev.map(vf =>
                                    vf.uid === newVideoFile.uid ? { ...vf, status: 'done' as const } : vf
                                ));
                            })
                            .catch(error => {
                                setVideoFiles(prev => prev.map(vf =>
                                    vf.uid === newVideoFile.uid ? { ...vf, status: 'error' as const } : vf
                                ));
                                console.error('视频文件上传失败:', error);
                            });
                    })
                    .catch(() => {
                        setVideoFiles(prev => prev.map(vf =>
                            vf.uid === newVideoFile.uid
                                ? { ...vf, decodeFailed: true, status: 'done' as const }
                                : vf
                        ));
                    });
                break;
            }

            case 'script':
                console.log('replaceExistingFile - 处理剧本文件:', newFile.name);

                // 移除同名剧本文件
                setScriptFiles(prev => {
                    const filtered = prev.filter(file => file.name !== fileName);
                    return filtered;
                });

                // 添加新剧本文件
                const newScriptFile: ScriptFile = {
                    uid: `${Date.now()}-${Math.random()}`,
                    name: newFile.name,
                    size: newFile.size,
                    type: newFile.type,
                    status: 'done' as const,
                    file: newFile
                };

                setScriptFiles(prev => [...prev, newScriptFile]);

                // 记录最近添加的剧本文件，用于取消时删除
                setRecentlyAddedScriptFile(newScriptFile);
                console.log('replaceExistingFile - 设置 recentlyAddedScriptFile:', newScriptFile);
                
                // 新增：立即上传剧本文件到后端（异步执行，不阻塞UI）
                uploadScriptToBackend([newFile]).catch(error => {
                    console.error('剧本文件上传失败:', error);
                });

                // 新增：替换剧本文件后也要检查文件名
                if (seriesName && seriesName !== '未知剧集' && seriesName !== '未选择剧集' && seriesName !== '获取失败') {
                    const scriptCheck = checkScriptFileName(newFile.name);
                    if (!scriptCheck.isValid) {
                        console.log('replaceExistingFile - 剧本文件名检查失败，显示弹窗');
                        // 显示剧本文件名提醒弹窗
                        setAlertTitle('提醒');
                        setAlertMessage(scriptCheck.message);
                        setAlertModalVisibleK(true); // 改为使用 alertModalVisibleK
                    }
                }
                break;

            case 'subtitle':
                // 移除同名字幕文件
                setSubtitleFiles(prev => {
                    const filtered = prev.filter(file => file.name !== fileName);
                    return filtered;
                });

                // 添加新字幕文件
                const newSubtitleFile: SubtitleFile = {
                    uid: `${Date.now()}-${Math.random()}`,
                    name: newFile.name,
                    size: newFile.size,
                    type: newFile.type,
                    status: 'uploading' as const,
                    file: newFile
                };

                setSubtitleFiles(prev => [...prev, newSubtitleFile]);
                
                // 新增：立即上传字幕文件到后端（异步执行，不阻塞UI）
                uploadSubtitleToBackend([newFile])
                    .then((resourceIds) => {
                        // 上传成功，更新状态并保存资源ID
                        setSubtitleFiles(prev => prev.map(sf => 
                            sf.uid === newSubtitleFile.uid ? { 
                                ...sf, 
                                status: 'done' as const,
                                resourceId: resourceIds?.[0]
                            } : sf
                        ));
                        message.success(`${newFile.name} 上传成功`, 5);
                    })
                    .catch(error => {
                        // 上传失败（code 非 0），从列表中移除该文件
                        setSubtitleFiles(prev => prev.filter(sf => sf.uid !== newSubtitleFile.uid));
                        console.error('字幕文件上传失败:', error);
                        message.error(`${newFile.name} 上传失败`);
                    });
                break;
        }
    };

    // 修改：视频上传的 beforeUpload 函数
    const videoUploadProps = {
        name: 'video',
        multiple: true,
        accept: 'video/*,.mp4,.avi,.mov,.mkv,.wmv,.flv,.webm,.m4v', // 只接受视频格式
        fileList: videoFiles,
        showUploadList: false,
        beforeUpload: (file: any, fileList: any[]) => {
            // 直接处理整个文件列表
            handleFileSelection(Array.from(fileList), 'video');
            return false; // 阻止自动上传
        },
        onChange: (info: any) => {
            // 这里不需要处理，因为我们在 beforeUpload 中已经处理了
        }
    };

    // 修改：剧本上传的 beforeUpload 函数
    const scriptUploadProps = {
        name: 'script',
        multiple: true,
        accept: '.doc,.docx', // 只接受 doc 和 docx 格式
        fileList: scriptFiles,
        beforeUpload: (file: any, fileList: any[]) => {
            // 直接处理整个文件列表
            handleFileSelection(Array.from(fileList), 'script');
            return false; // 阻止自动上传
        },
        onChange: (info: any) => {
            // 只在文件被移除时更新状态
            if (info.file.status === 'removed') {
                setScriptFiles(prev => prev.filter(f => f.uid !== info.file.uid));
            }
        },
        onRemove: (file: any) => {
            setScriptFiles(prev => prev.filter(f => f.uid !== file.uid));
        }
    };

    // 修改：字幕上传的 beforeUpload 函数
    const subtitleUploadProps = {
        name: 'subtitle',
        multiple: true,
        accept: '.srt', // 只接受 srt 格式
        fileList: subtitleFiles,
        beforeUpload: (file: any, fileList: any[]) => {
            // 直接处理整个文件列表
            handleFileSelection(Array.from(fileList), 'subtitle');
            return false; // 阻止自动上传
        },
        onChange: (info: any) => {
            // 只在文件被移除时更新状态
            if (info.file.status === 'removed') {
                setSubtitleFiles(prev => prev.filter(f => f.uid !== info.file.uid));
            }
        },
        onRemove: (file: any) => {
            setSubtitleFiles(prev => prev.filter(f => f.uid !== file.uid));
        }
    };

    const handleSubtitleTooltipToggle = () => {
        setShowSubtitleTooltip(!showSubtitleTooltip);
    };

    // 修改：检查视频文件是否缺少字幕文件的函数
    const getMissingSubtitleVideos = (): string[] => {
        // 移除 uploadSubtitle 检查，只要有视频文件就检查字幕
        if (videoFiles.length === 0) {
            return [];
        }

        // 获取所有视频文件名（不含扩展名）
        const videoNames = videoFiles.map(vf => vf.name.replace(/\.[^/.]+$/, ""));
        // 获取所有字幕文件名（不含扩展名）
        const subtitleNames = subtitleFiles.map(sf => sf.name.replace(/\.[^/.]+$/, ""));

        // 找出缺少字幕文件的视频文件
        return videoNames.filter(videoName => !subtitleNames.includes(videoName));
    };

    // 修改：检查指定视频文件是否缺少字幕文件
    const isVideoMissingSubtitle = (videoName: string): boolean => {
        // 只有当上传了字幕文件时，才检查是否缺少字幕文件
        if (subtitleFiles.length === 0 && !uploadSubtitle) {
            return false;
        }
        const missingVideos = getMissingSubtitleVideos();
        const videoNameWithoutExt = videoName.replace(/\.[^/.]+$/, "");
        return missingVideos.includes(videoNameWithoutExt);
    };

    // 修改：渲染视频上传区域
    const renderVideoUploadArea = (): React.ReactElement => {
        // 优化：在加载期间显示优化的加载状态
        if (isLoadingVideos) {
            return (
                <div className="video-loading-container">
                    <div className="loading-spinner">
                        <div className="spinner"></div>
                    </div>
                    <p className="loading-text">
                        {isPreloading ? '正在准备视频数据...' : '正在加载等待处理的视频...'}
                    </p>
                    {/* 优化：显示加载提示 */}
                    <div className="loading-tips">
                        <p>💡 提示：首次加载可能需要几秒钟，请耐心等待</p>
                    </div>
                </div>
            );
        }

        if (videoFiles.length === 0) {
            return (
                <Upload.Dragger {...videoUploadProps} className="video-upload-dragger">
                    <div className="upload-icon-container">
                        <CloudUploadOutlined className="upload-icon" />
                    </div>
                    <p className="ant-upload-text">批量上传视频文件到此处</p>
                    <p className="ant-upload-hint">
                        支持MP4、AVI、MOV等格式，单个文件&lt;2GB
                    </p>
                    <Button type="primary" className="select-file-btn" icon={<UploadOutlined />}>
                        选择视频文件
                    </Button>
                </Upload.Dragger>
            );
        }

        return (
            <div className="video-upload-area">
                {/* 左侧：继续上传和视频列表 */}
                <div className="video-upload-left">
                    {/* 视频预览网格 */}
                    <div className="video-preview-grid">
                        {/* 继续添加按钮 - 第一行第一列 */}
                        <div className="add-more-video-card">
                            <Upload {...videoUploadProps} showUploadList={false}>
                                <div className="add-more-content">
                                    <CloudUploadOutlined className="add-more-icon" />
                                    <span className="add-more-text">继续添加视频文件</span>
                                </div>
                            </Upload>
                        </div>

                        {/* 优化：视频预览缩略图，支持懒加载和性能优化 */}
                        {videoFiles.slice(visibleRange.start, visibleRange.end).map((file, index) => {
                            const actualIndex = visibleRange.start + index;
                            const isMissingSubtitle = isVideoMissingSubtitle(file.name);
                            const isUploading = file.status === 'uploading';
                            const isError = file.status === 'error';
                            const isDone = file.status === 'done';
                            
                            return (
                                <div
                                    key={file.uid}
                                    className={`video-preview-item ${selectedVideoIndex === actualIndex ? 'active' : ''}`}
                                    onClick={() => handleVideoSelect(actualIndex)}
                                >
                                    <div className="video-thumbnail">
                                        {/* 优化：使用懒加载的背景图片 */}
                                        {file.thumbnail && (
                                            <div
                                                className="background-image"
                                                style={{
                                                    backgroundImage: `url(${file.thumbnail})`
                                                }}
                                            ></div>
                                        )}

                                        {/* 优化：使用懒加载的缩略图 */}
                                        {file.thumbnail ? (
                                            <img
                                                src={file.thumbnail}
                                                alt={file.name}
                                                className="video-thumbnail-image"
                                                loading="lazy" // 添加懒加载
                                                onError={(e) => {
                                                    // 图片加载失败时隐藏
                                                    e.currentTarget.style.display = 'none';
                                                }}
                                            />
                                        ) : file.decodeFailed ? (
                                            <div className="decode-failed-thumbnail">
                                                <span className="decode-failed-thumbnail-text">解码失败</span>
                                            </div>
                                        ) : (
                                            <div className="video-placeholder">
                                                <VideoCameraOutlined className="video-icon" />
                                            </div>
                                        )}

                                        {/* 优化：状态覆盖层；解码失败的视频不上传，不显示上传进度 */}
                                        {isUploading && !file.decodeFailed && (() => {
                                            const fileKey = `video-${file.name}-${file.size}`;
                                            const progress = uploadProgress.get(fileKey) || 0;
                                            return (
                                                <div className="upload-progress-overlay">
                                                    <div className="upload-progress-content">
                                                        <div className="upload-text">{progress}%</div>
                                                        <div className="upload-progress-bar-container">
                                                            <div 
                                                                className="upload-progress-bar"
                                                                style={{ width: `${progress}%` }}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {isError && (
                                            <div
                                                className="upload-error-overlay"
                                                role="button"
                                                tabIndex={0}
                                                aria-label="上传失败"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRetryVideoUpload(file);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleRetryVideoUpload(file);
                                                    }
                                                }}
                                            >
                                                <div className="upload-error-content">
                                                    <img src="/img/load.png" alt="" className="upload-error-icon" />
                                                    <div className="upload-error-text">上传失败</div>
                                                </div>
                                            </div>
                                        )}

                                        {isMissingSubtitle && isDone && (
                                            <div className="missing-subtitle-overlay">
                                                <div className="missing-subtitle-text">缺少字幕文件</div>
                                            </div>
                                        )}

                                        {/* 优化：条件渲染，减少DOM操作 */}
                                        {!isMissingSubtitle && !isUploading && !isError && (
                                            <div className="play-icon-overlay">
                                                <PlayCircleOutlined className="play-icon" />
                                            </div>
                                        )}

                                        {/* 删除按钮 */}
                                        <div
                                            className="delete-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveVideo(file.uid);
                                            }}
                                        >
                                            <DeleteOutlined className="delete-icon" />
                                        </div>

                                        {/* 播放按钮和时长 */}
                                        <div className="play-duration-overlay">
                                            <PlayCircleOutlined className="play-icon-small" />
                                            <span>{file.duration ? formatDuration(file.duration) : '00:00'}</span>
                                        </div>
                                    </div>

                                    {/* 视频文件名 */}
                                    <div className="video-file-name" title={file.name}>
                                        {file.name}
                                    </div>
                                </div>
                            );
                        })}
                        
                        {/* 优化：如果还有更多视频，显示按钮 */}
                        {videoFiles.length > visibleRange.end && (
                            <div 
                                className="add-more-video-card"
                                onClick={() => {
                                    setVisibleRange(prev => ({
                                        start: prev.start,
                                        end: Math.min(prev.end + 11, videoFiles.length)
                                    }));
                                }}
                            >
                                <div className="add-more-content">
                                    <span className="add-more-text">
                                        点击加载剩余{videoFiles.length - visibleRange.end}个视频
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* 右侧：视频播放器 */}
                <div className="video-player-right">
                    <div className="video-player-container">
                        {selectedVideoIndex >= 0 && videoFiles[selectedVideoIndex] ? (
                            <div className="video-player">
                                <div className="video-screen">
                                    <video
                                        ref={el => { videoRefs.current[selectedVideoIndex] = el; }}
                                        src={
                                            videoFiles[selectedVideoIndex].previewUrl
                                                ?? (videoFiles[selectedVideoIndex].file
                                                    ? videoFiles[selectedVideoIndex].previewUrl
                                                    : processVideoUrl(videoFiles[selectedVideoIndex].url))
                                        }
                                        controls={!videoFiles[selectedVideoIndex].decodeFailed}
                                        className="video-element"
                                        poster={videoFiles[selectedVideoIndex].thumbnail}
                                        onError={() => {
                                            setVideoFiles(prev => prev.map((vf, i) =>
                                                i === selectedVideoIndex ? { ...vf, decodeFailed: true } : vf
                                            ));
                                        }}
                                    />
                                    {videoFiles[selectedVideoIndex].decodeFailed && (
                                        <div className="video-decode-failed-overlay">解码失败</div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="video-player">
                                <div className="video-screen">
                                    <div className="video-placeholder-large">
                                        <VideoCameraOutlined className="video-icon-large" />
                                    </div>
                                    <div className="video-controls">
                                        <div className="control-bar">
                                            <div className="play-pause-btn">
                                                <div className="play-triangle-large"></div>
                                            </div>
                                            <div className="time-display">0:00 / 0:00</div>
                                            <div className="progress-bar">
                                                <div className="progress-fill"></div>
                                            </div>
                                            <div className="control-icons">
                                                <div className="volume-icon">🔊</div>
                                                <div className="fullscreen-icon">⛶</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // 添加这个 useEffect 来监听条件变化
    useEffect(() => {
        if (!duplicateFileInfo?.fileName) {
            handleConfirmReplace();
        }
    }, [duplicateFileInfo?.fileName]);

    return (
        <div className="upload-video-page">
            {/* 新增：Alert提示组件 */}
            <AlertMessage
                config={alertConfig}
                onClose={hideAlert}
                zIndex={ALERT_Z_INDEX.UPLOAD_VIDEO}
            />

            {/* 优化的头部导航栏 */}
            <header className="upload-header" style={{ marginTop: '80px' }}>
                <div className="header-container">
                    <div className="header-left">
                        <BackButton onClick={handleBack} />
                        <span className="header-title">上传视频</span>
                    </div>
                    <div className="header-actions">
                        <Button
                            type="primary"
                            className="generate-btn"
                            onClick={handleGenerateVideo}
                            icon={<VideoCameraOutlined />}
                            size="large"
                            disabled={uploadingFiles.size > 0}
                        >
                            {uploadingFiles.size > 0 ? `上传中... (${uploadingFiles.size})` : '处理'}
                        </Button>
                    </div>
                </div>
            </header>

            {/* 主内容区 */}
            <main className="upload-main">
                <div className="upload-container">
                    {/* 左侧主要内容区域 */}
                    <div className="left-content">
                        {/* 合并后的卡片 */}
                        <Card className="upload-card" title="视频上传配置" bordered={false}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                {/* 视频名称 */}
                                <div>
                                    <h4 style={{ margin: '0 0 12px 0', color: '#333', fontSize: '16px' }}>
                                        视频名称
                                    </h4>
                                    <Input
                                        placeholder={
                                            videoFiles.length === 0
                                                ? "请先上传视频文件"
                                                : showVideoName
                                                    ? "点击视频展示视频名称"
                                                    : videoFiles.length === 1
                                                        ? "点击视频展示视频名称"
                                                        : "点击视频展示视频名称"
                                        }
                                        value={getVideoNameDisplayValue()}
                                        onChange={(e) => setVideoName(e.target.value)}
                                        size="large"
                                        className="video-name-input"
                                        prefix={<VideoCameraOutlined style={{ color: '#1890ff' }} />}
                                        disabled={isVideoNameDisabled}
                                    />
                                    {videoFiles.length > 1 && !showVideoName && (
                                        <div style={{
                                            fontSize: '12px',
                                            color: '#666',
                                            marginTop: '4px',
                                            fontStyle: 'italic'
                                        }}>
                                            提示：项目名称仅用于标识，视频文件将保持原始文件名
                                        </div>
                                    )}
                                    {showVideoName && (
                                        <div style={{
                                            fontSize: '12px',
                                            color: '#666',
                                            marginTop: '4px',
                                            fontStyle: 'italic'
                                        }}>
                                            提示：已显示视频名称
                                        </div>
                                    )}
                                </div>

                                {/* 视频上传区域 */}
                                <div>
                                    <h4 style={{  color: '#333', fontSize: '16px',marginBottom: '-12px'  }}>上传视频文件</h4>
                                    {renderVideoUploadArea()}
                                </div>
                            </div>
                        </Card>
                    </div>

                    {/* 右侧上传区域 */}
                    <div className="right-content">
                        {/* 上传剧本 */}
                        <Card
                            className={`upload-card ${scriptFiles.length > 0 && 'change'}`}
                            title="上传剧本"
                            bordered={false}
                        >
                            <Upload.Dragger
                                {...scriptUploadProps}
                                className="script-upload-dragger"
                            >

                                <div className="upload-icon-container">
                                    {scriptFiles.length > 0 || (<FileTextOutlined className="upload-icon" />)}

                                    {scriptFiles.length > 0 && (
                                        <div className="script-upload-hint-container">
                                            <FileTextOutlined className="upload-icon" />
                                            <div className="script-upload-hint-text-container">
                                                <div className="script-upload-hint-text">&nbsp;点击继续上传剧本文件</div>
                                                <div className="script-upload-hint">&nbsp;支持word等格式</div>
                                            </div>
                                        </div>

                                    )}
                                </div>

                                {scriptFiles.length > 0 || (
                                    <>
                                        <p className="ant-upload-text">上传剧本文件到此处</p>
                                        <p className="ant-upload-hint">
                                            支持word格式
                                        </p>
                                        <p className="ant-upload-note">
                                            请确保剧本文件与剧集名称一致
                                        </p>
                                    </>
                                )}


                            </Upload.Dragger>
                            {/* <div style={{ marginTop: 12 }}>
                                <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: -10, marginBottom: 15 }}>
                                    <span>剧本合并</span>
                                    <Switch size="small" />
                                </div>
                            </div> */}
                        </Card>

                        {/* 上传字幕 */}
                        <Card
                            className={`upload-card ${subtitleFiles.length > 0 && 'change1'}`}
                            title="上传字幕"
                            bordered={false}
                        >
                            <Upload.Dragger
                                {...subtitleUploadProps}
                                className="subtitle-upload-dragger"
                            >
                                <div className="upload-icon-container">
                                    {subtitleFiles.length > 0 || (<FileTextOutlined className="upload-icon" />)}

                                    {subtitleFiles.length > 0 && (
                                        <div className="script-upload-hint-container">
                                            <FileTextOutlined className="upload-icon" />
                                            <div className="script-upload-hint-text-container">
                                                <div className="script-upload-hint-text">&nbsp;点击继续上传字幕文件</div>
                                                <div className="script-upload-hint">&nbsp;支持srt格式</div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {subtitleFiles.length > 0 || (
                                    <> <p className="ant-upload-text">批量上传字幕文件到此处</p>
                                        <p className="ant-upload-hint">
                                            支持srt格式
                                        </p>
                                        <p className="ant-upload-note">
                                            批量上传时，请确保字幕文件与视频文件命名一致
                                            <div style={{ position: 'relative', display: 'inline-block' }}>
                                                <QuestionCircleOutlined
                                                    style={{
                                                        marginLeft: '8px',
                                                        color: '#999',
                                                        cursor: 'pointer',
                                                        fontSize: '14px'
                                                    }}
                                                    onMouseEnter={handleSubtitleTooltipToggle}
                                                    onMouseLeave={handleSubtitleTooltipToggle}
                                                />

                                                {/* 自定义气泡提示 */}
                                                {showSubtitleTooltip && (
                                                    <div style={{
                                                        position: 'fixed',
                                                        top: '47%',
                                                        left: '47%',
                                                        transform: 'translate(-50%, -50%)',
                                                        zIndex: 99999,
                                                        minWidth: '280px',
                                                        maxWidth: '320px',
                                                        pointerEvents: 'none'
                                                    }}>
                                                        {/* 气泡内容 */}
                                                        <div style={{
                                                            fontStyle: 'normal',
                                                            backgroundColor: '#ffffff',
                                                            borderRadius: '8px',
                                                            border: '1px solid #e9ecef',
                                                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                                                            padding: '16px',
                                                            textAlign: 'left',
                                                            pointerEvents: 'auto'
                                                        }}>
                                                            <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#000000', fontSize: '18px' }}>
                                                                字幕文件格式建议
                                                            </div>
                                                            <div style={{ marginBottom: '8px', color: '#666666' }}>
                                                                字幕文件需包含时间戳和字幕内容，如下图
                                                            </div>
                                                            <div style={{ marginTop: '12px' }}>
                                                                <div style={{ marginBottom: '12px' }}>
                                                                    <span style={{
                                                                        display: 'inline-block',
                                                                        backgroundColor: '#e9ecef',
                                                                        color: '#6c757d',
                                                                        padding: '4px 8px',
                                                                        borderRadius: '4px',
                                                                        fontSize: '12px',
                                                                        marginRight: '8px'
                                                                    }}>时间戳</span>
                                                                    <span style={{ color: '#495057' }}>00:00:00,360 → 00:00:02,120</span>
                                                                </div>
                                                                <div style={{ marginBottom: '12px' }}>
                                                                    <span style={{
                                                                        display: 'inline-block',
                                                                        backgroundColor: '#e9ecef',
                                                                        color: '#6c757d',
                                                                        padding: '4px 8px',
                                                                        borderRadius: '4px',
                                                                        fontSize: '12px',
                                                                        marginRight: '8px'
                                                                    }}>字幕内容</span>
                                                                    <span style={{ color: '#495057' }}>字幕的内容</span>
                                                                </div>
                                                                <div style={{ marginBottom: '12px' }}>
                                                                    <span style={{
                                                                        display: 'inline-block',
                                                                        backgroundColor: '#e9ecef',
                                                                        color: '#6c757d',
                                                                        padding: '4px 8px',
                                                                        borderRadius: '4px',
                                                                        fontSize: '12px',
                                                                        marginRight: '8px'
                                                                    }}>时间戳</span>
                                                                    <span style={{ color: '#495057' }}>00:00:03,140 → 00:00:03,520</span>
                                                                </div>
                                                                <div style={{ marginBottom: '12px' }}>
                                                                    <span style={{
                                                                        display: 'inline-block',
                                                                        backgroundColor: '#e9ecef',
                                                                        color: '#6c757d',
                                                                        padding: '4px 8px',
                                                                        borderRadius: '4px',
                                                                        fontSize: '12px',
                                                                        marginRight: '8px'
                                                                    }}>字幕内容</span>
                                                                    <span style={{ color: '#495057' }}>字幕的内容</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </p>

                                    </>
                                )}

                            </Upload.Dragger>
                        </Card>
                    </div>
                </div>
            </main>

            {/* 生成确认弹窗 */}
            <Modal
                title="请确认信息"
                open={generateModalVisible}
                onOk={handleConfirmGenerate}
                onCancel={handleCancelGenerate}
                confirmLoading={isGenerating}
                okText="确认"
                cancelText="取消"
                width={600}
                className="generate-modal"
                style={{
                    zIndex: 9999,
                    position: 'relative'
                }}
                maskStyle={{ zIndex: 9998 }}
                wrapClassName="generate-modal-wrap"
            >
                <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#333', fontSize: '16px' }}>剧集名称</h4>
                    <Input
                        placeholder="剧集名称"
                        value={seriesName}
                        size="large"
                        className="video-name-input"
                        prefix={<VideoCameraOutlined style={{ color: '#1890ff' }} />}
                        disabled
                    />
                </div>
                <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#333', fontSize: '16px' }}>目标语言</h4>
                    <Input
                        placeholder="目标语言"
                        value={targetLanguage}
                        size="large"
                        disabled
                        suffix={<span style={{ color: '#999' }}>▼</span>}
                    />
                </div>
                <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#333', fontSize: '16px' }}>字幕擦除</h4>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Switch
                            checked={subtitleErase}
                            disabled
                            checkedChildren="是"
                            unCheckedChildren="否"
                        />

                    </div>
                </div>
                <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#333', fontSize: '16px' }}>字幕文件</h4>
                    <Input
                        placeholder="字幕文件"
                        value={subtitleFiles.length > 0 ? '已上传' : '未上传'}
                        size="large"
                        disabled
                    />
                </div>
                <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#333', fontSize: '16px' }}>剧本文件</h4>
                    <Input
                        placeholder="剧本文件"
                        value={scriptFiles.length > 0 ? '已上传' : '未上传'}
                        size="large"
                        disabled
                    />
                </div>
            </Modal>

            {/* 提醒弹窗 */}
            <Modal
                title={alertTitle}
                open={alertModalVisible}
                onOk={handleAlertConfirm}
                onCancel={handleAlertCancel}
                okText="确认"
                cancelText="取消"
                width={500}
                className="alert-modal"
                style={{
                    zIndex: 10000,
                    position: 'relative',
                    paddingBottom: '0px'
                }}
                maskStyle={{ zIndex: 9999 }}
                wrapClassName="alert-modal-wrap"
            >
                <div style={{
                    padding: '20px 0',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    color: '#333'
                }}>
                    {alertMessage.split('\n').map((line, index) => (
                        <div key={index} style={{
                            marginBottom: index === 0 ? '12px' : '8px',
                            fontWeight: index === 0 ? 'normal' : 'normal',
                            color: index === 0 ? '#333' : '#666'
                        }}>
                            {line}
                        </div>
                    ))}
                </div>
            </Modal>

            <Modal
                title={alertTitle}
                open={alertModalVisibleK}
                onOk={handleAlertConfirmK}
                onCancel={handleAlertCancelK}
                okText="确认"
                cancelText="取消"
                width={500}
                className="alert-modal"
                style={{
                    zIndex: 10000,
                    position: 'relative'
                }}
                maskStyle={{ zIndex: 9999 }}
                wrapClassName="alert-modal-wrap"
            >
                <div style={{
                    padding: '20px 0',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    color: '#333'
                }}>
                    {alertMessage.split('\n').map((line, index) => (
                        <div key={index} style={{
                            marginBottom: index === 0 ? '12px' : '8px',
                            fontWeight: index === 0 ? 'normal' : 'normal',
                            color: index === 0 ? '#333' : '#666'
                        }}>
                            {line}
                        </div>
                    ))}
                </div>
            </Modal>

            {/* 新增：字幕匹配不成功弹窗 */}
            <Modal
                title="匹配不成功"
                open={subtitleMatchModalVisible}
                onOk={handleSubtitleMatchConfirm}
                onCancel={handleSubtitleMatchCancel}
                okText="确定"
                cancelText="取消"
                width={500}
                className="subtitle-match-modal"
                style={{
                    zIndex: 10001,
                    position: 'relative'
                }}
                maskStyle={{ zIndex: 10000 }}
                wrapClassName="subtitle-match-modal-wrap"
            >
                {subtitleMatchInfo && (
                    <div style={{
                        padding: '20px 0',
                        fontSize: '14px',
                        lineHeight: '1.6',
                        color: '#333'
                    }}>
                        <p style={{ marginBottom: '16px' }}>
                            检测到当前多传了以下字幕文件：
                        </p>
                        <div style={{
                            backgroundColor: '#fff2f0',
                            border: '1px solid #ffccc7',
                            padding: '12px',
                            borderRadius: '6px',
                            marginBottom: '16px'
                        }}>
                            {subtitleMatchInfo.invalidFiles.map((invalid, index) => (
                                <p key={index} style={{
                                    margin: '4px 0',
                                    color: '#333',
                                    fontSize: '14px'
                                }}>
                                    {invalid.name}.srt
                                </p>
                            ))}
                        </div>
                        <p style={{ marginBottom: '0', color: '#666' }}>
                            是否删除该字幕文件？
                        </p>
                    </div>
                )}
            </Modal>

            {/* 修改：同名文件确认弹窗 */}
            {duplicateFileInfo?.fileName ? (
                <Modal
                    title={"你上传的文件存在同名文件"}
                    open={duplicateFileModalVisible}
                    onOk={handleConfirmReplace}
                    onCancel={handleCancelReplace}
                    okText={"替换文件"}
                    cancelText="取消"
                    width={500}
                    className="duplicate-file-modal"
                    style={{
                        zIndex: 10001,
                        position: 'relative'
                    }}
                    maskStyle={{ zIndex: 10000 }}
                    wrapClassName="duplicate-file-modal-wrap"
                >
                    <div style={{
                        padding: '20px 0',
                        fontSize: '14px',
                        lineHeight: '1.6',
                        color: '#333'
                    }}>
                        {pendingFileBatch?.hasDuplicate && pendingFileBatch.duplicateFileNames.length > 0 ? (
                            <>
                                <p style={{ marginBottom: '16px' }}>
                                    发现以下重复文件：
                                </p>
                                <div style={{
                                    backgroundColor: '#fff2f0',
                                    border: '1px solid #ffccc7',
                                    padding: '12px',
                                    borderRadius: '6px',
                                    marginBottom: '16px'
                                }}>
                                    {pendingFileBatch.duplicateFileNames.map((fileName, index) => (
                                        <p key={index} style={{
                                            margin: '4px 0',
                                            color: '#ff4d4f',
                                            fontWeight: 'bold'
                                        }}>
                                            • {fileName}
                                        </p>
                                    ))}
                                </div>
                                <p style={{ marginBottom: '16px' }}>
                                    是否替换这些重复文件？
                                </p>
                            </>
                        ) : (
                            <p style={{ marginBottom: '16px' }}>
                                是否替换"{duplicateFileInfo.fileName}"?
                            </p>
                        )}

                        <div style={{
                            backgroundColor: '#f5f5f5',
                            padding: '12px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            color: '#666'
                        }}>
                            <p style={{ margin: '0 0 8px 0' }}>
                                <strong>文件类型：</strong>
                                {duplicateFileInfo.fileType === 'video' ? '视频文件' :
                                    duplicateFileInfo.fileType === 'script' ? '剧本文件' : '字幕文件'}
                            </p>
                            <p style={{ margin: '0' }}>
                                <strong>当前文件：</strong>
                                {duplicateFileInfo.existingFile?.name}
                                ({duplicateFileInfo.existingFile?.size ?
                                    `${(duplicateFileInfo.existingFile.size / 1024 / 1024).toFixed(2)} MB` :
                                    '未知大小'})
                            </p>
                            <p style={{ margin: '8px 0 0 0', color: '#1890ff', fontSize: '12px' }}>
                                <strong>提示：</strong>
                                点击"替换文件"将替换同名文件，保留不同名文件，然后继续上传
                            </p>
                            <p style={{ margin: '8px 0 0 0', color: '#ff4d4f', fontSize: '12px' }}>
                                <strong>注意：</strong>
                                点击"取消"将删除非重复文件，保留之前上传的重复文件
                            </p>
                        </div>
                    </div>
                </Modal>
            ) : null}

        </div>
    );
};
